/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Button,
    useTheme,
    AppBar,
    Toolbar,
    IconButton,
    Chip,
    Popover,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    Menu,
    MenuItem,
    Alert,
    Tooltip,
    Divider,
    Skeleton,
    Tabs,
    Tab,
    Badge,
    Grid,
    Paper,
    Collapse,
    useMediaQuery,
    Checkbox,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import TripPhotosSection from '../components/TripPhotosSection';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import ShareIcon from '@mui/icons-material/Share';
import PeopleIcon from '@mui/icons-material/People';
import PublicIcon from '@mui/icons-material/Public';
import { useFeatureFlags } from '../contexts/FeatureFlagContext';
import ComingSoonBadge from '../components/common/ComingSoonBadge';
import AddTripItemDialog from '../components/AddTripItemDialog';
import TripItemImportCard from '../components/trips/TripItemImportCard';
import LockIcon from '@mui/icons-material/Lock';
import GroupIcon from '@mui/icons-material/Group';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import HotelIcon from '@mui/icons-material/Hotel';
import FlightIcon from '@mui/icons-material/Flight';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AttractionsIcon from '@mui/icons-material/Attractions';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ChatIcon from '@mui/icons-material/Chat';
import PollIcon from '@mui/icons-material/Poll';
import HistoryIcon from '@mui/icons-material/History';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { api, isAuthenticated } from '../utils/api';
import { trackEvent } from '../utils/analytics';
import AiResultsReviewModal, { AiTripPlan } from '../components/AiResultsReviewModal';
import SnackbarMui from '@mui/material/Snackbar';
import TripIntelligenceSection from '../components/trips/TripIntelligenceSection';
import ItineraryTimeline from '../components/trips/ItineraryTimeline';
import HotelSearchDialog from '../components/search/HotelSearchDialog';
import FlightSearchDialog from '../components/search/FlightSearchDialog';
import CarSearchDialog from '../components/search/CarSearchDialog';
import ActivitySearchDialog from '../components/search/ActivitySearchDialog';
import { SidebarAd } from '../components/ads';
import { usePlan } from '../hooks/usePlan';
import PlanUpgradeModal from '../components/PlanUpgradeModal';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import { formatDate, parseLocalDate, toLocalDateString, todayLocalString, daysBetween } from '../utils/dateHelpers';
interface Trip {
    tripId: string;
    userId: string;
    title: string;
    destination: string;
    startDate?: string;
    endDate?: string;
    description?: string;
    status: string;
    visibility: 'private' | 'bffs_travel_partners' | 'friends_family' | 'public';
    sharedWith?: string[];
    createdAt: string;
    updatedAt: string;
    itinerary: any[];
    isGroup?: boolean;
    groupName?: string;
    costSharingModel?: string;
    rsvpDeadline?: string;
    travelers?: number;
    travelerCount?: number;
    estimatedTravelers?: string | number;
    adults?: number;
    children?: number;
    infants?: number;
    importSource?: string;
    isCompanion?: boolean;
    isOwner?: boolean;
    companionType?: string;
    ownerName?: string;
    companionCount?: number;
    sharingPanel?: any[];
    pendingPhotos?: number;
    bookings?: {
        flights?: any[];
        hotels?: any[];
        cars?: any[];
        activities?: any[];
    };
}

interface TripItem {
    id: string;
    itemId?: string;
    item_type?: string;
    itemType?: string;
    title: string;
    location?: string;
    start_date?: string;
    startDate?: string;
    end_date?: string;
    endDate?: string;
    start_time?: string;
    startTime?: string;
    end_time?: string;
    endTime?: string;
    estimated_cost?: number;
    estimatedCost?: number;
    booking_status?: string;
    bookingStatus?: string;
    notes?: string;
    preferences?: any;
}

interface TripPhoto {
    id: string;
    url: string;
    thumbnail: string;
    caption?: string;
    aiTags?: string[];
    uploadedAt: string;
}

interface Friend {
    id: string;
    name: string;
    email: string;
    avatar?: string;
    status?: string;
}

// Edit Item Dialog
interface ItemDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (item: any) => Promise<void>;
    itemType: 'hotel' | 'flight' | 'activity' | 'transport';
    editItem?: TripItem | null;
    destination?: string;
    startDate?: string;
    endDate?: string;
}

function ItemDialog({ isOpen, onClose, onSave, itemType, editItem, destination, startDate, endDate }: ItemDialogProps) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [formData, setFormData] = useState({
        title: '',
        location: destination || '',
        startDate: startDate || '',
        endDate: endDate || '',
        startTime: '',
        endTime: '',
        estimatedCost: '',
        notes: '',
        bookingStatus: 'researching',
    });

    useEffect(() => {
        if (isOpen) {
            if (editItem) {
                setFormData({
                    title: editItem.title || '',
                    location: editItem.location || destination || '',
                    startDate: (editItem.start_date || editItem.startDate || startDate || '').split('T')[0],
                    endDate: (editItem.end_date || editItem.endDate || endDate || '').split('T')[0],
                    startTime: editItem.start_time || editItem.startTime || '',
                    endTime: editItem.end_time || editItem.endTime || '',
                    estimatedCost: (editItem.estimated_cost || editItem.estimatedCost || '').toString(),
                    notes: editItem.notes || '',
                    bookingStatus: editItem.booking_status || editItem.bookingStatus || 'researching',
                });
            } else {
                setFormData({
                    title: '',
                    location: destination || '',
                    startDate: startDate ? startDate.split('T')[0] : '',
                    endDate: endDate ? endDate.split('T')[0] : '',
                    startTime: '',
                    endTime: '',
                    estimatedCost: '',
                    notes: '',
                    bookingStatus: 'researching',
                });
            }
            setError('');
        }
    }, [isOpen, editItem, destination, startDate, endDate]);

    const getTitle = () => {
        const action = editItem ? 'Edit' : 'Add';
        switch (itemType) {
            case 'hotel': return `${action} Hotel`;
            case 'flight': return `${action} Flight`;
            case 'activity': return `${action} Activity`;
            case 'transport': return `${action} Transport`;
            default: return `${action} Item`;
        }
    };

    const getIcon = () => {
        switch (itemType) {
            case 'hotel': return <HotelIcon sx={{ color: '#4A6FA5' }} />;
            case 'flight': return <FlightIcon sx={{ color: '#2196F3' }} />;
            case 'activity': return <AttractionsIcon sx={{ color: '#2D6E6A' }} />;
            case 'transport': return <DirectionsCarIcon sx={{ color: '#607D8B' }} />;
            default: return null;
        }
    };

    const getPlaceholder = () => {
        switch (itemType) {
            case 'hotel': return 'e.g., Hilton Nairobi or Safari Lodge';
            case 'flight': return 'e.g., United ORD to NBO or Flight to Nairobi';
            case 'activity': return 'e.g., Safari Day Tour or Hot Air Balloon';
            case 'transport': return 'e.g., Airport Transfer or Safari Vehicle';
            default: return 'Enter title';
        }
    };

    const handleSave = async () => {
        if (!formData.title.trim()) {
            setError('Title is required');
            return;
        }
        setLoading(true);
        setError('');
        try {
            await onSave({
                item_type: itemType === 'transport' ? 'car' : itemType,
                title: formData.title,
                location: formData.location || undefined,
                start_date: formData.startDate || undefined,
                end_date: formData.endDate || undefined,
                start_time: formData.startTime || undefined,
                end_time: formData.endTime || undefined,
                estimated_cost: formData.estimatedCost ? parseFloat(formData.estimatedCost) : undefined,
                notes: formData.notes || undefined,
                booking_status: formData.bookingStatus,
            });
            onClose();
        } catch (err: any) {
            setError(err.message || 'Failed to save');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
                {getIcon()}
                {getTitle()}
            </DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <TextField
                    fullWidth
                    label="Title *"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder={getPlaceholder()}
                    sx={{ mb: 2, mt: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />

                <TextField
                    fullWidth
                    label="Location"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="e.g., Nairobi, Kenya"
                    sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />

                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label={itemType === 'hotel' ? 'Check-in Date' : 'Date'}
                            type="date"
                            value={formData.startDate}
                            onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        {itemType === 'hotel' ? (
                            <TextField
                                fullWidth
                                label="Check-out Date"
                                type="date"
                                value={formData.endDate}
                                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ min: formData.startDate || undefined }}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                        ) : (
                            <TextField
                                fullWidth
                                label="Time"
                                type="time"
                                value={formData.startTime}
                                onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                        )}
                    </Grid>
                </Grid>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Estimated Cost"
                            type="number"
                            value={formData.estimatedCost}
                            onChange={(e) => setFormData({ ...formData, estimatedCost: e.target.value })}
                            InputProps={{ startAdornment: <Typography sx={{ mr: 0.5, color: '#999' }}>$</Typography> }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={formData.bookingStatus}
                                label="Status"
                                onChange={(e) => setFormData({ ...formData, bookingStatus: e.target.value })}
                            >
                                <MenuItem value="researching">Researching</MenuItem>
                                <MenuItem value="quoted">Quoted</MenuItem>
                                <MenuItem value="booked">Booked</MenuItem>
                                <MenuItem value="confirmed">Confirmed</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>

                <TextField
                    fullWidth
                    label="Notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    multiline
                    rows={2}
                    placeholder="Booking reference, special requests, etc."
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
            </DialogContent>
            <DialogActions sx={{ p: 2.5 }}>
                <Button onClick={onClose} disabled={loading} sx={{ textTransform: 'none' }}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={loading || !formData.title.trim()}
                    startIcon={loading ? <CircularProgress size={16} /> : <CheckIcon />}
                    sx={{ textTransform: 'none', borderRadius: 2, px: 3 }}
                >
                    {loading ? 'Saving...' : 'Save Changes'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// Item Card Component
interface ItemCardProps {
    item: TripItem;
    itemType: 'hotel' | 'flight' | 'activity' | 'transport';
    onEdit: () => void;
    onDelete: () => void;
    canEdit: boolean;
}

function ItemCard({ item, itemType, onEdit, onDelete, canEdit }: ItemCardProps) {
    const getIcon = () => {
        switch (itemType) {
            case 'hotel': return <HotelIcon sx={{ color: '#4A6FA5', fontSize: 20 }} />;
            case 'flight': return <FlightIcon sx={{ color: '#2196F3', fontSize: 20 }} />;
            case 'activity': return <AttractionsIcon sx={{ color: '#2D6E6A', fontSize: 20 }} />;
            case 'transport': return <DirectionsCarIcon sx={{ color: '#607D8B', fontSize: 20 }} />;
        }
    };

    const getStatusColor = (status?: string) => {
        switch (status) {
            case 'booked':
            case 'confirmed': return 'success';
            case 'quoted': return 'info';
            default: return 'default';
        }
    };

    const status = item.booking_status || item.bookingStatus;
    const cost = item.estimated_cost || item.estimatedCost;
    const sDate = item.start_date || item.startDate;
    const eDate = item.end_date || item.endDate;
    const sTime = item.start_time || item.startTime;

    return (
        <Card variant="outlined" sx={{ borderRadius: 2, mb: 1.5 }}>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Box sx={{ mt: 0.5 }}>{getIcon()}</Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }} noWrap>
                                {item.title}
                            </Typography>
                            {status && (
                                <Chip
                                    label={status}
                                    size="small"
                                    color={getStatusColor(status) as any}
                                    sx={{ height: 20, fontSize: '0.7rem', textTransform: 'capitalize' }}
                                />
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                            {item.location && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <LocationOnOutlinedIcon sx={{ fontSize: 14 }} /> {item.location}
                                </Typography>
                            )}
                            {sDate && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <CalendarTodayOutlinedIcon sx={{ fontSize: 14 }} />
                                    {formatDate(sDate, { month: 'short', day: 'numeric' })}{eDate && ` - ${formatDate(eDate, { month: 'short', day: 'numeric' })}`}
                                </Typography>
                            )}
                            {sTime && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <AccessTimeIcon sx={{ fontSize: 14 }} /> {sTime}
                                </Typography>
                            )}
                            {cost && (
                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                    ${Number(cost).toLocaleString()}
                                </Typography>
                            )}
                        </Box>
                        {item.notes && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                                {item.notes}
                            </Typography>
                        )}
                    </Box>
                    {canEdit && (
    <Box sx={{ display: 'flex', gap: 0.5 }}>
        <IconButton size="small" onClick={onEdit} sx={{ color: 'text.secondary' }}>
            <EditOutlinedIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={onDelete} sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" />
        </IconButton>
    </Box>
)}
                </Box>
            </CardContent>
        </Card>
    );
}

// Empty State Component
interface EmptyStateProps {
    itemType: 'hotel' | 'flight' | 'activity' | 'transport';
    onAdd: () => void;
    canEdit: boolean;
    bookingEnabled?: boolean;
    onBlockedClick?: () => void;
    onUpload?: () => void;
}

function EmptyState({ itemType, onAdd, canEdit, bookingEnabled = true, onBlockedClick, onUpload }: EmptyStateProps) {
    const getIcon = () => {
        switch (itemType) {
            case 'hotel': return <HotelIcon sx={{ fontSize: 48, color: '#4A6FA5', opacity: 0.5 }} />;
            case 'flight': return <FlightIcon sx={{ fontSize: 48, color: '#2196F3', opacity: 0.5 }} />;
            case 'activity': return <AttractionsIcon sx={{ fontSize: 48, color: '#2D6E6A', opacity: 0.5 }} />;
            case 'transport': return <DirectionsCarIcon sx={{ fontSize: 48, color: '#607D8B', opacity: 0.5 }} />;
        }
    };

    const getMessage = () => {
        switch (itemType) {
            case 'hotel': return 'No hotels added yet';
            case 'flight': return 'No flights added yet';
            case 'activity': return 'No activities planned yet';
            case 'transport': return 'No transportation added yet';
        }
    };

    const getButtonText = () => {
        if (bookingEnabled === false) {
            switch (itemType) {
                case 'hotel': return 'Add Hotel';
                case 'flight': return 'Add Flight';
                case 'activity': return 'Add Activity';
                case 'transport': return 'Add Transport';
            }
        }
        switch (itemType) {
            case 'hotel': return 'Search Hotels';
            case 'flight': return 'Search Flights';
            case 'activity': return 'Search Activities';
            case 'transport': return 'Search Cars';
        }
    };

    return (
        <Card variant="outlined" sx={{ borderStyle: 'dashed', borderRadius: 3, borderColor: '#ddd' }}>
            <CardContent sx={{ textAlign: 'center', py: 5 }}>
                {getIcon()}
                <Typography color="text.secondary" sx={{ mt: 2, mb: 2 }}>
                    {getMessage()}
                </Typography>
                {canEdit && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={onAdd}
                            sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                flexWrap: 'wrap',
                                maxWidth: '100%',
                            }}
                        >
                            {getButtonText()}
                        </Button>
                        {onUpload && (
                            <Button
                                size="small"
                                variant="text"
                                startIcon={<CloudUploadIcon />}
                                onClick={onUpload}
                                sx={{ textTransform: 'none', color: '#2D6E6A' }}
                            >
                                Upload Booking
                            </Button>
                        )}
                    </Box>
                )}
            </CardContent>
        </Card>
    );
}

// Planning Tab Panel
interface PlanningTabProps {
    items: TripItem[];
    itemType: 'hotel' | 'flight' | 'activity' | 'transport';
    loading: boolean;
    onAdd: () => void;
    onEdit: (item: TripItem) => void;
    onDelete: (itemId: string) => void;
    canEdit: boolean;
    bookingEnabled?: boolean;
    onBlockedClick?: () => void;
    onUpload?: () => void;
}

function PlanningTab({ items, itemType, loading, onAdd, onEdit, onDelete, canEdit, bookingEnabled, onBlockedClick, onUpload }: PlanningTabProps) {
    if (loading) {
        return (
            <Box sx={{ py: 3 }}>
                <Skeleton variant="rounded" height={80} sx={{ mb: 1.5, borderRadius: 2 }} />
                <Skeleton variant="rounded" height={80} sx={{ mb: 1.5, borderRadius: 2 }} />
            </Box>
        );
    }

    if (items.length === 0) {
        return <EmptyState itemType={itemType} onAdd={onAdd} canEdit={canEdit} bookingEnabled={bookingEnabled} onBlockedClick={onBlockedClick} onUpload={onUpload} />;
    }

    const getButtonText = () => {
        switch (itemType) {
            case 'hotel': return 'Search More Hotels';
            case 'flight': return 'Search More Flights';
            case 'activity': return 'Search More Activities';
            case 'transport': return 'Search More Cars';
        }
    };

    return (
        <Box>
            {items.map((item) => (
                <ItemCard
                    key={item.id || item.itemId}
                    item={item}
                    itemType={itemType}
                    onEdit={() => onEdit(item)}
                    onDelete={() => onDelete(item.id || item.itemId || '')}
                    canEdit={canEdit}
                />
            ))}
            {canEdit && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mt: 1, gap: 0.5 }}>
                    <Button
                        startIcon={<AddIcon />}
                        onClick={onAdd}
                        sx={{ textTransform: 'none' }}
                    >
                        {getButtonText()}
                    </Button>
                    {onUpload && (
                        <Button
                            size="small"
                            variant="text"
                            startIcon={<CloudUploadIcon />}
                            onClick={onUpload}
                            sx={{ textTransform: 'none', color: '#2D6E6A' }}
                        >
                            Upload Booking
                        </Button>
                    )}
                </Box>
            )}
        </Box>
    );
}

// MAIN COMPONENT
const TripDetailPage: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { tripId } = useParams<{ tripId: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
    const today = todayLocalString();

    const [trip, setTrip] = useState<Trip | null>(null);
    const { canUse } = usePlan();
    const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
    const [upgradeFeatureName, setUpgradeFeatureName] = useState('');
    const { isEnabled } = useFeatureFlags();
    const masterPublicEnabled = isEnabled('master_public');
    const masterFriendsEnabled = isEnabled('master_friends');
    const publicTripsEnabled = masterPublicEnabled && isEnabled('public_trips');
    const friendsTripsEnabled = masterFriendsEnabled && isEnabled('friends_trips');
    const bookingEnabled = isEnabled('booking_enabled') === true;
    const bookingTripDetailsHeader = isEnabled('booking_trip_details_header') === true;
    const bookingTripDetailsFooter = isEnabled('booking_trip_details_footer') === true;
    const showLocalEvents = isEnabled('local_events_section') === true;
    // #236 Phase A1: admin-controllable. Card hides when flag is off.
    const aiActivityRecommendationsEnabled = isEnabled('ai_activity_recommendations') === true;
    const showBookHeader = bookingEnabled && bookingTripDetailsHeader;
    const showBookFooter = bookingEnabled && bookingTripDetailsFooter;
    const [uploadActive, setUploadActive] = useState(false);
    const [previousActiveTab, setPreviousActiveTab] = useState<number>(0);
    const handleBookingBlocked = () => {
        setSnackbar({ open: true, message: 'Booking is coming soon - stay tuned!' });
    };
    const [addItemOpen, setAddItemOpen] = useState(false);
    const [addItemDefaultType, setAddItemDefaultType] = useState<'flight' | 'hotel' | 'car' | 'activity'>('hotel');
    const [tripItems, setTripItems] = useState<TripItem[]>([]);
    const [photos, setPhotos] = useState<TripPhoto[]>([]);
    const [loading, setLoading] = useState(true);
    const [itemsLoading, setItemsLoading] = useState(true);
    const [error, setError] = useState('');
    const [idCopied, setIdCopied] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '' });

    // Planning tabs
    const [activeTab, setActiveTab] = useState(0);

    // Edit dialog (for editing existing items)
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editDialogType, setEditDialogType] = useState<'hotel' | 'flight' | 'activity' | 'transport'>('hotel');
    const [editingItem, setEditingItem] = useState<TripItem | null>(null);

    // Search dialogs (for adding new items)
    const [hotelSearchOpen, setHotelSearchOpen] = useState(false);
    const [flightSearchOpen, setFlightSearchOpen] = useState(false);
    const [carSearchOpen, setCarSearchOpen] = useState(false);
    const [activitySearchOpen, setActivitySearchOpen] = useState(false);

    // AI Activity Recommendations
    const [aiActivityLoading, setAiActivityLoading] = useState(false);
    const [showAiActivityReview, setShowAiActivityReview] = useState(false);
    const [aiActivityPlan, setAiActivityPlan] = useState<AiTripPlan | null>(null);

    // Expandable sections state
    const [overviewExpanded, setOverviewExpanded] = useState(false);
    const [showTripIntelligence, setShowTripIntelligence] = useState(false);
    const [showItinerary, setShowItinerary] = useState(false);

    // Trip Intelligence active section
    const [tripIntelligenceSection, setTripIntelligenceSection] = useState<'weather' | 'packing' | 'requirements' | 'airline' | 'hotel' | 'transport' | null>(null);

    // Trip Planning collapse state - start collapsed
    const [tripPlanningExpanded, setTripPlanningExpanded] = useState(false);

    // Edit dialog
    const [editOpen, setEditOpen] = useState(false);
    const [editLoading, setEditLoading] = useState(false);
    const [editError, setEditError] = useState('');
    const [editExpiresAt, setEditExpiresAt] = useState('');
    const [editData, setEditData] = useState({
        title: '',
        destination: '',
        startDate: '',
        endDate: '',
        description: '',
        visibility: 'private' as 'private' | 'bffs_travel_partners' | 'friends_family' | 'public',
        status: 'planning',
    });

    // Delete dialog
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Share dialog
    const [shareOpen, setShareOpen] = useState(false);
    const [tripMenuAnchorEl, setTripMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [idPopoverAnchorEl, setIdPopoverAnchorEl] = useState<null | HTMLElement>(null);
    const [shareLoading, setShareLoading] = useState(false);
    const [shareNote, setShareNote] = useState('');
    const [shareError, setShareError] = useState('');
    const [selectedFriends, setSelectedFriends] = useState<string[]>([]);
    const [linkCopied, setLinkCopied] = useState(false);
    const [friends, setFriends] = useState<Friend[]>([]);
    const [friendsLoading, setFriendsLoading] = useState(false);
    // Travel Companion share fields
    const [inviteAsCompanion, setInviteAsCompanion] = useState(false);
    const [shareExpiresAt, setShareExpiresAt] = useState('');
    const [notifyBeforeExpiry, setNotifyBeforeExpiry] = useState(false);
    const [notifyDaysBefore, setNotifyDaysBefore] = useState<1 | 3 | 7>(3);
    const [askPhotoAction, setAskPhotoAction] = useState(false);
    const [newShareEmails, setNewShareEmails] = useState<{name: string; email: string}[]>([]);
    const [newShareName, setNewShareName] = useState('');
    const [newShareEmail, setNewShareEmail] = useState('');
    // Sharing panel collapse
    const [sharingPanelExpanded, setSharingPanelExpanded] = useState(false);
    const [sharingPanelAnchorEl, setSharingPanelAnchorEl] = useState<HTMLElement | null>(null);

    // Fetch Trip
    const fetchTrip = useCallback(async () => {
        try {
            const response = await api.get(`/trips/${tripId}`);
            const data = await response.json();
            if (response.ok && data.success) {
                setTrip(data.trip);
                setPhotos(data.trip.photos || []);
                setEditData({
                    title: data.trip.title,
                    destination: data.trip.destination,
                    startDate: data.trip.startDate || '',
                    endDate: data.trip.endDate || '',
                    description: data.trip.description || '',
                    visibility: data.trip.visibility || 'private',
                    status: data.trip.status,
                });
                setSelectedFriends(data.trip.sharedWith || []);
            } else {
                setError(data.error || 'Trip not found');
            }
        } catch (err) {
            setError('Network error');
        } finally {
            setLoading(false);
        }
    }, [tripId]);

    // Fetch Trip Items
    const fetchTripItems = useCallback(async () => {
        setItemsLoading(true);
        try {
            const response = await api.get(`/trips/${tripId}/items`);
            const data = await response.json();
            if (response.ok && data.success) {
                const mappedItems = (data.items || []).map((item: any) => ({
                    ...item,
                    id: item.item_id || item.id,
                    itemId: item.item_id || item.itemId,
                    tripId: item.trip_id || item.tripId,
                    itemType: item.item_type || item.itemType,
                    startDate: item.start_date || item.startDate,
                    endDate: item.end_date || item.endDate,
                    startTime: item.start_time || item.startTime,
                    endTime: item.end_time || item.endTime,
                    estimatedCost: item.estimated_cost || item.estimatedCost,
                    bookingStatus: item.booking_status || item.bookingStatus,
                    createdAt: item.created_at || item.createdAt,
                    updatedAt: item.updated_at || item.updatedAt,
                }));
                setTripItems(mappedItems);
            }
        } catch (err) {
            console.error('Failed to fetch trip items:', err);
        } finally {
            setItemsLoading(false);
        }
    }, [tripId]);

    // Fetch Friends
    const fetchFriends = useCallback(async () => {
        setFriendsLoading(true);
        try {
            const response = await api.get('/friends/list');
            const data = await response.json();
            if (response.ok && data.success) {
                setFriends(
                    (data.friends || []).map((f: any) => ({
                        id: f.friendId || f.id || f.userId,
                        name: f.name || f.displayName || f.email,
                        email: f.email || '',
                        avatar: f.avatar || f.profilePicture || undefined,
                        status: f.status,
                    }))
                );
            }
        } catch (err) {
            console.log('Friends API not available');
        } finally {
            setFriendsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!isAuthenticated()) {
            navigate('/login');
            return;
        }
        // Clear prior trip's data before loading this one, so navigating
        // between trips never renders the previous trip's bookings/items
        // (e.g. stale hasHotels flashing another trip's Hotel Info).
        setTrip(null);
        setTripItems([]);
        fetchTrip();
        fetchTripItems();
    }, [fetchTrip, fetchTripItems, navigate]);

    // Handle ?openSearch= query parameter from booking page "Find Alternative"
    useEffect(() => {
        const openSearch = searchParams.get('openSearch');
        const openUploadParam = searchParams.get('openUpload');
        if (openUploadParam === '1' && trip) {
            setSearchParams({});
            setPreviousActiveTab(0);
            setUploadActive(true);
            setTripPlanningExpanded(true);
            scrollToSection('trip-planning-upload-area');
            return;
        }
        if (openSearch && trip) {
            setSearchParams({});
            switch (openSearch) {
                case 'flights':
                    setFlightSearchOpen(true);
                    break;
                case 'hotels':
                    setHotelSearchOpen(true);
                    break;
                case 'cars':
                    setCarSearchOpen(true);
                    break;
                case 'activities':
                    setActivitySearchOpen(true);
                    break;
            }
        }
    }, [searchParams, setSearchParams, trip]);

    // Computed Values
    const isOwner = trip?.isOwner ?? (() => {
        const storedUser = localStorage.getItem('user');
        if (!storedUser) return false;
        try {
            const user = JSON.parse(storedUser);
            return user.userId === trip?.userId;
        } catch { return false; }
    })();

// Extract items from imported trip's bookings JSON
const importedFlights = (trip?.bookings?.flights || []).map((f: any, idx: number) => {
    // Handle both flat structure (f.departureDate) and nested structure (f.departure.date) from Bedrock
    const departureDate = f.departure?.date || f.departureDate || f.date;
    const departureTime = f.departure?.time || f.departureTime || f.time;
    const departureAirport = f.departure?.airport || f.departureAirport || f.origin || '';
    const departureCity = f.departure?.city || f.departureCity || '';
    const arrivalDate = f.arrival?.date || f.arrivalDate;
    const arrivalAirport = f.arrival?.airport || f.arrivalAirport || f.destination || '';
    const arrivalCity = f.arrival?.city || f.arrivalCity || '';
    
    // Build origin and destination strings
    const origin = departureAirport || departureCity || f.origin || '';
    const destination = arrivalAirport || arrivalCity || f.destination || '';
    
    // Build route string from available data
    const route = f.route || 
        (origin && destination ? `${origin} → ${destination}` : '') ||
        '';
    
    return {
        id: `imported-flight-${idx}`,
        item_type: 'flight',
        title: f.airline ? `${f.airline} ${f.flightNumber || ''}`.trim() : f.title || 'Flight',
        location: route.trim() || undefined,
        start_date: departureDate || trip?.startDate,
        end_date: arrivalDate,
        start_time: departureTime,
        notes: f.confirmationNumber ? `Confirmation: ${f.confirmationNumber}` : '',
        booking_status: 'confirmed',
    };
});

const importedHotels = (trip?.bookings?.hotels || []).map((h: any, idx: number) => ({
    id: `imported-hotel-${idx}`,
    item_type: 'hotel',
    title: h.name || h.title || 'Hotel',
    location: h.location || h.address,
    start_date: h.checkIn || h.startDate,
    end_date: h.checkOut || h.endDate,
    notes: h.confirmationNumber ? `Confirmation: ${h.confirmationNumber}` : '',
    booking_status: 'confirmed',
}));

const importedActivities = (trip?.bookings?.activities || []).map((a: any, idx: number) => ({
    id: `imported-activity-${idx}`,
    item_type: 'activity',
    title: a.name || a.title || 'Activity',
    location: a.location,
    start_date: a.date || a.startDate,
    start_time: a.time || a.startTime,
    notes: a.confirmationNumber ? `Confirmation: ${a.confirmationNumber}` : '',
    booking_status: 'confirmed',
}));

const importedCars = (trip?.bookings?.cars || []).map((c: any, idx: number) => ({
    id: `imported-car-${idx}`,
    item_type: 'car',
    title: c.company ? `${c.company} - ${c.type || 'Rental'}` : c.title || 'Car Rental',
    location: c.pickupLocation || c.location,
    start_date: c.pickupDate || c.startDate,
    end_date: c.dropoffDate || c.endDate,
    notes: c.confirmationNumber ? `Confirmation: ${c.confirmationNumber}` : '',
    booking_status: 'confirmed',
}));

// Filter items by type - include both tripItems AND imported bookings
const hotels = [...tripItems.filter(i => (i.item_type || i.itemType) === 'hotel'), ...importedHotels];
const flights = [...tripItems.filter(i => (i.item_type || i.itemType) === 'flight'), ...importedFlights];
const activities = [...tripItems.filter(i => (i.item_type || i.itemType) === 'activity'), ...importedActivities];
const transport = [...tripItems.filter(i => ['car', 'transport'].includes(i.item_type || i.itemType || '')), ...importedCars];

// Combined items for itinerary
const allItems = [...tripItems, ...importedFlights, ...importedHotels, ...importedActivities, ...importedCars];

// Total estimated cost
    const totalCost = tripItems.reduce((sum, item) => sum + (Number(item.estimated_cost) || Number(item.estimatedCost) || 0), 0);

    // Can show book button - hide if already booked, cancelled, or date-completed.
    // TODO post-MVP: extract MyTripsPage's getStatus() to a shared helper to avoid duplicating this lifecycle logic.
    const isDateCompleted = !!trip?.endDate && (() => {
        const end = parseLocalDate(trip.endDate);
        return end ? end < new Date(new Date().setHours(0, 0, 0, 0)) : false;
    })();
    const canBook = tripItems.length > 0
        && !['booked', 'completed', 'cancelled', 'booking'].includes(trip?.status || '')
        && !isDateCompleted;
    // Upload card visibility — wider than canBook because empty trips are exactly when Upload is most useful.
    const canShowUploadCard = bookingEnabled
        && !isDateCompleted
        && !['cancelled', 'deleted'].includes(trip?.status || '');

    // Lifecycle archived = trip is over (date or status). Hides Trip Planning + Upload affordances, expands Trip Overview as record.
    // TODO post-MVP: extract MyTripsPage's getStatus() to a shared helper to avoid duplicating this lifecycle logic.
    const isLifecycleArchived = isDateCompleted
        || ['completed', 'cancelled'].includes(trip?.status || '');

    // Smart primary CTA — context-aware single action for trip header card
    const primaryCTA: 'book' | 'share' | 'add_photos' | null = (() => {
        if (!isOwner) return null;
        if (['cancelled', 'deleted'].includes(trip?.status || '')) return null;
        if (isLifecycleArchived) {
            return photos.length === 0 ? 'add_photos' : 'share';
        }
        if (bookingEnabled && tripItems.length === 0) return 'book';
        return 'share';
    })();

    // Default-expand Trip Overview on archived trips (user can still collapse afterward).
    // Intentionally exclude `trip` from deps — this is a one-time default that should NOT re-fire
    // on trip refetch (which would override user's manual collapse action).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { if (trip && isLifecycleArchived) { setOverviewExpanded(true); } }, [tripId, isLifecycleArchived]);

    // Display status — mirrors MyTripsPage getStatus() lifecycle priority. TODO post-MVP: extract to shared helper.
    const getDisplayStatus = (): { label: string; color: 'default' | 'primary' | 'success' | 'warning' | 'info' | 'error' } => {
        if (!trip) return { label: 'Planning', color: 'default' };
        if (trip.status === 'cancelled') return { label: 'Cancelled', color: 'error' };
        if (trip.status === 'deleted') return { label: 'Deleted', color: 'default' };
        if (trip.status === 'booked' || trip.status === 'booking') return { label: 'Booked', color: 'success' };
        if (!trip.startDate) return { label: 'Planning', color: 'default' };
        const now = new Date();
        const start = parseLocalDate(trip.startDate);
        const end = trip.endDate ? parseLocalDate(trip.endDate) : start;
        if (!start) return { label: 'Planning', color: 'default' };
        if (now >= start && (!end || now <= end)) return { label: 'Ongoing', color: 'success' };
        if (now < start) {
            const days = Math.ceil((start.getTime() - now.getTime()) / 86400000);
            return days <= 7 ? { label: `${days}d away`, color: 'warning' } : { label: 'Upcoming', color: 'info' };
        }
        return { label: 'Completed', color: 'default' };
    };
    const displayStatus = getDisplayStatus();

    // Scroll to Section Helper
    const scrollToSection = (sectionId: string) => {
        setTimeout(() => {
            const element = document.getElementById(sectionId);
            if (element) {
                const headerOffset = 70;
                const elementPosition = element.getBoundingClientRect().top;
                const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
            }
        }, 150);
    };

    // Check if international destination
    const isInternational = (() => {
        if (!trip?.destination) return false;
        const destination = trip.destination.toLowerCase();
        const domesticKeywords = ['usa', 'united states', 'us,', 'america', 'u.s.'];
        const usStates = ['alabama', 'alaska', 'arizona', 'arkansas', 'california', 'colorado', 'connecticut', 'delaware', 'florida', 'georgia', 'hawaii', 'idaho', 'illinois', 'indiana', 'iowa', 'kansas', 'kentucky', 'louisiana', 'maine', 'maryland', 'massachusetts', 'michigan', 'minnesota', 'mississippi', 'missouri', 'montana', 'nebraska', 'nevada', 'new hampshire', 'new jersey', 'new mexico', 'new york', 'north carolina', 'north dakota', 'ohio', 'oklahoma', 'oregon', 'pennsylvania', 'rhode island', 'south carolina', 'south dakota', 'tennessee', 'texas', 'utah', 'vermont', 'virginia', 'washington', 'west virginia', 'wisconsin', 'wyoming'];
        const usCities = ['chicago', 'los angeles', 'san francisco', 'miami', 'houston', 'phoenix', 'philadelphia', 'san antonio', 'san diego', 'dallas', 'seattle', 'denver', 'boston', 'las vegas', 'orlando', 'atlanta', 'fort lauderdale', 'tampa', 'austin', 'nashville', 'new orleans', 'portland', 'san jose', 'sacramento', 'charlotte', 'indianapolis', 'columbus', 'detroit', 'memphis', 'baltimore', 'milwaukee', 'albuquerque', 'tucson', 'fresno', 'mesa', 'kansas city', 'long beach', 'virginia beach', 'oakland', 'minneapolis', 'tulsa', 'arlington', 'wichita', 'raleigh', 'honolulu', 'anchorage'];
        return !domesticKeywords.some(k => destination.includes(k)) &&
            !usStates.some(s => destination.includes(s)) &&
            !usCities.some(c => destination.includes(c));
    })();

    // Open Add Dialog: manual-add when booking is off, search-based when booking is on.
    const openAddDialog = (type: 'hotel' | 'flight' | 'activity' | 'transport') => {
        if (!bookingEnabled) {
            // Map TripDetailPage's 'transport' to AddTripItemDialog's 'car' type.
            const dialogType: 'flight' | 'hotel' | 'car' | 'activity' =
                type === 'transport' ? 'car' : type;
            setAddItemDefaultType(dialogType);
            setAddItemOpen(true);
            return;
        }
        switch (type) {
            case 'hotel':
                setHotelSearchOpen(true);
                break;
            case 'flight':
                setFlightSearchOpen(true);
                break;
            case 'activity':
                setActivitySearchOpen(true);
                break;
            case 'transport':
                setCarSearchOpen(true);
                break;
        }
    };

    // Open Edit Dialog (for editing existing items)
    const openEditDialog = (item: TripItem) => {
        const type = (item.item_type || item.itemType || 'activity') as any;
        setEditDialogType(type === 'car' ? 'transport' : type);
        setEditingItem(item);
        setEditDialogOpen(true);
    };

    // Save Item (for edit dialog)
    const handleSaveItem = async (itemData: any) => {
        if (editingItem) {
            const response = await api.put(`/trips/${tripId}/items/${editingItem.id || editingItem.itemId}`, itemData);
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to update');
        }
        await fetchTripItems();
        setSnackbar({ open: true, message: 'Item updated!' });
    };

    // Handle Search Dialog Selections
    const handleHotelsSelected = async (selectedHotels: any[]) => {
        try {
            for (const hotel of selectedHotels) {
                await api.post(`/trips/${tripId}/items`, {
                    item_type: 'hotel',
                    title: hotel.name,
                    location: hotel.location,
                    start_date: trip?.startDate,
                    end_date: trip?.endDate,
                    estimated_cost: hotel.totalPrice,
                    notes: `${hotel.starRating} stars - ${hotel.reviewScore}/10 - ${hotel.amenities?.join(', ') || ''}`,
                    booking_status: 'researching',
                });
            }
            await fetchTripItems();
            setSnackbar({ open: true, message: `${selectedHotels.length} hotel${selectedHotels.length > 1 ? 's' : ''} added!` });
        } catch (err) {
            setSnackbar({ open: true, message: 'Failed to add hotels' });
        }
    };

    const handleFlightsSelected = async (selectedFlights: any[], flightSearchParams?: { origin?: string; destination?: string; passengers?: number }) => {
        try {
            for (const flight of selectedFlights) {
                const origin = flight.origin || flight.departureCity || flight.from || flightSearchParams?.origin || '';
                const destination = flight.destination || flight.arrivalCity || flight.to || flightSearchParams?.destination || trip?.destination || '';

                // Build flight title and notes based on round-trip or one-way
                const isRoundTrip = !!flight.returnFlight;
                const outboundInfo = `${flight.flightNumber || ''} ${flight.departureTime || ''}-${flight.arrivalTime || ''}`.trim();
                const returnInfo = isRoundTrip ? `${flight.returnFlight.flightNumber || ''} ${flight.returnFlight.departureTime || ''}-${flight.returnFlight.arrivalTime || ''}`.trim() : '';
                
                const title = isRoundTrip
                    ? `${flight.airline || 'Flight'} - ${origin} ↔ ${destination} (Round Trip)`
                    : `${flight.airline || 'Flight'} ${flight.flightNumber || ''} - ${origin} to ${destination}`;

                const notes = isRoundTrip
                    ? `Outbound: ${outboundInfo} (${flight.duration || ''}, ${flight.stops === 0 ? 'Direct' : `${flight.stops} stop(s)`}) | Return: ${returnInfo} (${flight.returnFlight.duration || ''}) | ${flight.cabinClass || 'Economy'}`
                    : `${flight.cabinClass || 'Economy'} - ${flight.stops === 0 ? 'Direct' : `${flight.stops} stop${flight.stops > 1 ? 's' : ''}`} - ${flight.duration || ''}`.trim();

                // Create ONE flight item (handles both round-trip and one-way)
                await api.post(`/trips/${tripId}/items`, {
                    item_type: 'flight',
                    title: title,
                    location: origin && destination ? `${origin} to ${destination}` : undefined,
                    start_date: flight.departureDate || flight.departure_date || trip?.startDate,
                    end_date: isRoundTrip ? (flight.returnDate || flight.return_date || trip?.endDate) : undefined,
                    estimated_cost: flight.price || flight.totalPrice,
                    notes: notes,
                    booking_status: 'researching',
                });
            }

            if (flightSearchParams?.passengers && flightSearchParams.passengers > 1) {
                await api.put(`/trips/${tripId}`, {
                    title: trip?.title,
                    destination: trip?.destination,
                    start_date: trip?.startDate,
                    end_date: trip?.endDate,
                    travelers: flightSearchParams.passengers,
                }).catch(err => console.log('Failed to update traveler count:', err));

                setTrip(prev => prev ? { ...prev, travelers: flightSearchParams.passengers } : prev);
            }

            await fetchTripItems();
            setSnackbar({ open: true, message: `${selectedFlights.length} flight${selectedFlights.length > 1 ? 's' : ''} added!` });
        } catch (err) {
            setSnackbar({ open: true, message: 'Failed to add flights' });
        }
    };

    const handleCarsSelected = async (cars: any[]) => {
        try {
            for (const car of cars) {
                await api.post(`/trips/${tripId}/items`, {
                    item_type: 'car',
                    title: `${car.carName} (${car.company})`,
                    location: car.pickupLocation,
                    start_date: trip?.startDate,
                    end_date: trip?.endDate,
                    estimated_cost: car.totalPrice,
                    notes: `${car.carType} - ${car.transmission} - ${car.passengers} passengers`,
                    booking_status: 'researching',
                });
            }
            await fetchTripItems();
            setSnackbar({ open: true, message: `${cars.length} car${cars.length > 1 ? 's' : ''} added!` });
        } catch (err) {
            setSnackbar({ open: true, message: 'Failed to add cars' });
        }
    };

    const handleActivitiesSelected = async (selectedActivities: any[]) => {
        try {
            for (const activity of selectedActivities) {
                await api.post(`/trips/${tripId}/items`, {
                    item_type: 'activity',
                    title: activity.title,
                    location: activity.location,
                    start_date: trip?.startDate,
                    estimated_cost: activity.price,
                    notes: `${activity.duration} - ${activity.rating} stars (${activity.reviewCount} reviews) - via ${activity.provider}`,
                    booking_status: 'researching',
                });
            }
            await fetchTripItems();
            setSnackbar({ open: true, message: `${selectedActivities.length} activit${selectedActivities.length > 1 ? 'ies' : 'y'} added!` });
        } catch (err) {
            setSnackbar({ open: true, message: 'Failed to add activities' });
        }
    };

    const handleDeleteItem = async (itemId: string) => {
        if (!window.confirm('Delete this item?')) return;
        try {
            await api.delete(`/trips/${tripId}/items/${itemId}`);
            await fetchTripItems();
            setSnackbar({ open: true, message: 'Item deleted' });
        } catch (err) {
            setSnackbar({ open: true, message: 'Failed to delete item' });
        }
    };

    // Other Handlers
    const handleCopyTripId = () => {
        navigator.clipboard.writeText(tripId || '');
        setIdCopied(true);
        setTimeout(() => setIdCopied(false), 2000);
    };

    const handleEditChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setEditData(prev => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (field: string) => (e: any) => {
        setEditData(prev => ({ ...prev, [field]: e.target.value }));
    };

    const handleEditSubmit = async () => {
        if (!isAuthenticated()) return;
        setEditLoading(true);
        setEditError('');
        try {
            const response = await api.put(`/trips/${tripId}`, {
                ...editData,
                is_group: trip?.isGroup || false,
                expires_at: (editData.visibility !== 'private' && editExpiresAt) ? editExpiresAt : null,
            });
            const data = await response.json();
            if (response.ok && data.success) {
                setTrip(data.trip);
                setEditOpen(false);
                setSnackbar({ open: true, message: 'Trip updated!' });
            } else {
                setEditError(data.error || 'Failed to update trip');
            }
        } catch (err) {
            setEditError('Network error');
        } finally {
            setEditLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!isAuthenticated()) return;
        setDeleteLoading(true);
        try {
            const response = await api.delete(`/trips/${tripId}`);
            const data = await response.json();
            if (response.ok && data.success) {
                navigate('/trips');
            } else {
                setError(data.error || 'Failed to delete trip');
            }
        } catch (err) {
            setError('Network error');
        } finally {
            setDeleteLoading(false);
            setDeleteOpen(false);
        }
    };

    const handleOpenShare = () => {
        setShareOpen(true);
        setShareError('');
        setSelectedFriends(trip?.sharedWith || []);
        setShareNote(isLifecycleArchived ? `Hey! I wanted to share my ${trip?.title || 'trip'} with you — take a look! 🌍` : `Hey! I'm planning my ${trip?.title || 'trip'} and wanted to share it with you — take a look! 🌍`);
        setInviteAsCompanion(false);
        setShareExpiresAt('');
        setNotifyBeforeExpiry(false);
        setNotifyDaysBefore(3);
        setAskPhotoAction(false);
        setNewShareEmails([]);
        setNewShareName('');
        setNewShareEmail('');
        fetchFriends();
    };

    const handleCopyLink = () => {
        const shareUrl = `${window.location.origin}/trips/${tripId}`;
        navigator.clipboard.writeText(shareUrl);
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
    };

    const handleShareSubmit = async () => {
        if (selectedFriends.length === 0 && newShareEmails.length === 0) {
            setShareOpen(false);
            return;
        }
        setShareLoading(true);
        try {
            const token = localStorage.getItem('idToken');
            // Send friend invites for new emails
            for (const entry of newShareEmails) {
                await fetch(`${process.env.REACT_APP_API_URL || 'https://3yslsbk5n1.execute-api.us-east-1.amazonaws.com/prod'}/friends/invite`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify({ email: entry.email, name: entry.name, message: `${shareNote}` }),
                });
            }
            if (selectedFriends.length === 0 && newShareEmails.length > 0) {
                setShareOpen(false);
                setNewShareEmails([]);
                setSnackbar({ open: true, message: `Invitation sent to ${newShareEmails.length} new friend${newShareEmails.length !== 1 ? 's' : ''}! 🎉` });
                setShareLoading(false);
                return;
            }
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://3yslsbk5n1.execute-api.us-east-1.amazonaws.com/prod'}/trips/${tripId}/share`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    friend_ids: selectedFriends,
                    message: shareNote.trim() || `Check out my ${trip?.title || 'trip'}!`,
                    invite_as_companion: inviteAsCompanion,
                    expires_at: shareExpiresAt || undefined,
                    notify_before_expiry: notifyBeforeExpiry,
                    notify_days_before: notifyBeforeExpiry ? notifyDaysBefore : undefined,
                    ask_photo_action: askPhotoAction,
                }),
            });
            const data = await response.json();
            if (response.ok && data.success) {
                setTrip(prev => prev ? { ...prev, visibility: 'bffs_travel_partners', sharedWith: selectedFriends } : prev);
                trackEvent('trip_shared', { tripId, visibility: 'bffs_travel_partners' });
                setShareOpen(false);
                setSnackbar({ open: true, message: `Trip shared with ${data.sent} friend${data.sent !== 1 ? 's' : ''}! 🎉` });
            } else {
                setShareError(data.error || 'Failed to share');
            }
        } catch (err) {
            setShareError('Failed to share');
        } finally {
            setShareLoading(false);
        }
    };

    const handleBookTrip = () => {
        if (!bookingEnabled) {
            handleBookingBlocked();
            return;
        }
        navigate(`/trips/${tripId}/book`);
    };
// Print & Download PDF - Full Trip Details
const generateFullTripHTML = (forPDF: boolean = false) => {
    if (!trip) return '';
    
    const fmtDate = (d?: string) => d ? formatDate(d) : '';
    
    // Build flights section
    const flightsHTML = flights.length > 0 ? `
    <div class="section">
        <h3>✈️ Flights (${flights.length})</h3>
        ${flights.map((f: any) => `
            <div class="item">
                <div class="item-title">${f.title}</div>
                ${f.location ? `<div class="item-detail">Route: ${f.location}</div>` : ''}
                ${f.start_date || f.startDate ? `<div class="item-detail">Date: ${fmtDate(f.start_date || f.startDate)}${f.start_time || f.startTime ? ` at ${f.start_time || f.startTime}` : ''}</div>` : ''}
                ${f.notes ? `<div class="item-detail">${f.notes}</div>` : ''}
                ${(f.estimated_cost || f.estimatedCost) ? `<div class="item-cost">$${Number(f.estimated_cost || f.estimatedCost).toLocaleString()}</div>` : ''}
            </div>
        `).join('')}
    </div>
` : '';

    // Build hotels section
    const hotelsHTML = hotels.length > 0 ? `
        <div class="section">
            <h3>🏨 Hotels (${hotels.length})</h3>
            ${hotels.map((h: any) => `
                <div class="item">
                    <div class="item-title">${h.title}</div>
                    ${h.location ? `<div class="item-detail">Location: ${h.location}</div>` : ''}
                    ${h.start_date || h.startDate ? `<div class="item-detail">Check-in: ${fmtDate(h.start_date || h.startDate)}${h.end_date || h.endDate ? ` | Check-out: ${fmtDate(h.end_date || h.endDate)}` : ''}</div>` : ''}
                    ${h.notes ? `<div class="item-detail">${h.notes}</div>` : ''}
                    ${(h.estimated_cost || h.estimatedCost) ? `<div class="item-cost">$${Number(h.estimated_cost || h.estimatedCost).toLocaleString()}</div>` : ''}
                </div>
            `).join('')}
        </div>
    ` : '';

    // Build transport section
    const transportHTML = transport.length > 0 ? `
        <div class="section">
            <h3>🚗 Transportation (${transport.length})</h3>
            ${transport.map((c: any) => `
                <div class="item">
                    <div class="item-title">${c.title}</div>
                    ${c.location ? `<div class="item-detail">Location: ${c.location}</div>` : ''}
                    ${c.start_date || c.startDate ? `<div class="item-detail">Pick-up: ${fmtDate(c.start_date || c.startDate)}${c.end_date || c.endDate ? ` | Drop-off: ${fmtDate(c.end_date || c.endDate)}` : ''}</div>` : ''}
                    ${c.notes ? `<div class="item-detail">${c.notes}</div>` : ''}
                    ${(c.estimated_cost || c.estimatedCost) ? `<div class="item-cost">$${Number(c.estimated_cost || c.estimatedCost).toLocaleString()}</div>` : ''}
                </div>
            `).join('')}
        </div>
    ` : '';

    // Build activities section
    const activitiesHTML = activities.length > 0 ? `
        <div class="section">
            <h3>🎯 Activities (${activities.length})</h3>
            ${activities.map((a: any) => `
                <div class="item">
                    <div class="item-title">${a.title}</div>
                    ${a.location ? `<div class="item-detail">Location: ${a.location}</div>` : ''}
                    ${a.start_date || a.startDate ? `<div class="item-detail">Date: ${fmtDate(a.start_date || a.startDate)}${a.start_time || a.startTime ? ` at ${a.start_time || a.startTime}` : ''}</div>` : ''}
                    ${a.notes ? `<div class="item-detail">${a.notes}</div>` : ''}
                    ${(a.estimated_cost || a.estimatedCost) ? `<div class="item-cost">$${Number(a.estimated_cost || a.estimatedCost).toLocaleString()}</div>` : ''}
                </div>
            `).join('')}
        </div>
    ` : '';

    // Build itinerary by day
    let itineraryHTML = '';
    if (allItems.length > 0 && trip.startDate && trip.endDate) {
        const startDateObj = parseLocalDate(trip.startDate)!;
        const endDateObj = parseLocalDate(trip.endDate)!;
        const days: { [key: string]: typeof allItems } = {};
        
        // Group items by date
        allItems.forEach((item: any) => {
            const itemDate = item.start_date || item.startDate;
            if (itemDate) {
                const dateKey = itemDate.split('T')[0];
                if (!days[dateKey]) days[dateKey] = [];
                days[dateKey].push(item);
            }
        });

        // Generate day-by-day itinerary
        let dayNum = 1;
        let itineraryDays = '';
        for (let d = new Date(startDateObj); d <= endDateObj; d.setDate(d.getDate() + 1)) {
            const dateKey = toLocalDateString(d);
            const dayItems = days[dateKey] || [];
            const dayLabel = d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
            
            itineraryDays += `
                <div class="day">
                    <div class="day-header">Day ${dayNum} - ${dayLabel}</div>
                    ${dayItems.length > 0 ? dayItems.map((item: any) => {
                        const type = item.item_type || item.itemType || 'activity';
                        const icon = type === 'flight' ? '✈️' : type === 'hotel' ? '🏨' : type === 'car' ? '🚗' : '🎯';
                        return `<div class="day-item">${icon} ${item.title}${item.start_time || item.startTime ? ` at ${item.start_time || item.startTime}` : ''}</div>`;
                    }).join('') : '<div class="day-item day-empty">No activities scheduled</div>'}
                </div>
            `;
            dayNum++;
        }
        
        if (itineraryDays) {
            itineraryHTML = `<div class="section"><h3>📅 Day-by-Day Itinerary</h3>${itineraryDays}</div>`;
        }
    }

    // Trip overview stats
    const overviewHTML = `
        <div class="overview">
            <div class="stat"><span class="stat-value">${tripDays}</span><span class="stat-label">Days</span></div>
            <div class="stat"><span class="stat-value">${allItems.length}</span><span class="stat-label">Items</span></div>
            <div class="stat"><span class="stat-value">$${totalCost.toLocaleString()}</span><span class="stat-label">Est. Total</span></div>
        </div>
    `;

    // Travel tips for international
    const travelTipsHTML = isInternational ? `
        <div class="section tips">
            <h3>🌍 Travel Information</h3>
            <div class="tip">• This is an international trip - check visa requirements</div>
            <div class="tip">• Verify passport validity (6+ months recommended)</div>
            <div class="tip">• Check COVID-19 entry requirements</div>
            <div class="tip">• Consider travel insurance</div>
            <div class="tip">• Notify your bank of travel dates</div>
        </div>
    ` : '';

    return `
        <!DOCTYPE html>
        <html>
        <head>
            <title>${trip.title} - Percoro Trip Details</title>
            <style>
                * { box-sizing: border-box; margin: 0; padding: 0; }
                body { font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif; padding: 40px; max-width: 850px; margin: 0 auto; color: #333; line-height: 1.5; font-size: 13px; }
                .logo { color: #2D6E6A; font-weight: 700; font-size: 28px; margin-bottom: 20px; }
                .instructions { background: #FFF9E6; border: 1px solid #FFE082; border-radius: 8px; padding: 12px 16px; margin-bottom: 20px; font-size: 12px; color: #666; }
                h1 { color: #2D6E6A; font-size: 24px; margin-bottom: 4px; }
                .destination { color: #666; font-size: 16px; margin-bottom: 12px; display: flex; align-items: center; gap: 6px; }
                .meta-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 20px; padding: 12px; background: #FAFAFA; border-radius: 8px; }
                .meta-item .meta-label { font-size: 10px; text-transform: uppercase; color: #999; margin-bottom: 2px; }
                .meta-item .meta-value { font-size: 13px; font-weight: 600; }
                .status-badge { display: inline-block; padding: 3px 10px; border-radius: 10px; font-size: 11px; font-weight: 600; background: #E8F5E9; color: #2E7D32; }
                .status-badge.info { background: #E3F2FD; color: #1565C0; }
                .status-badge.warning { background: #FFF3E0; color: #E65100; }
                .status-badge.error { background: #FFEBEE; color: #C62828; }
                .overview { display: flex; gap: 20px; margin-bottom: 20px; padding: 16px; background: linear-gradient(135deg, #E8F5F3 0%, #F0FAF8 100%); border-radius: 8px; border: 1px solid #B2DFDB; }
                .stat { text-align: center; flex: 1; }
                .stat-value { display: block; font-size: 22px; font-weight: 700; color: #2D6E6A; }
                .stat-label { font-size: 11px; color: #666; text-transform: uppercase; }
                .description { margin-bottom: 20px; padding: 12px; background: #F8F9FA; border-radius: 8px; border-left: 4px solid #2D6E6A; font-size: 13px; }
                .section { margin-bottom: 20px; page-break-inside: avoid; }
                .section h3 { font-size: 14px; color: #333; margin-bottom: 10px; padding-bottom: 6px; border-bottom: 2px solid #2D6E6A; }
                .item { padding: 10px 12px; background: #fff; border: 1px solid #E0E0E0; border-radius: 6px; margin-bottom: 6px; }
                .item-title { font-weight: 600; font-size: 13px; color: #333; }
                .item-detail { font-size: 12px; color: #666; margin-top: 2px; }
                .item-cost { font-size: 12px; color: #2D6E6A; font-weight: 600; margin-top: 4px; }
                .day { margin-bottom: 12px; padding: 10px; background: #FAFAFA; border-radius: 6px; }
                .day-header { font-weight: 600; font-size: 13px; color: #2D6E6A; margin-bottom: 6px; }
                .day-item { font-size: 12px; padding: 4px 0; border-bottom: 1px dashed #E0E0E0; }
                .day-item:last-child { border-bottom: none; }
                .day-empty { color: #999; font-style: italic; }
                .tips { background: #FFF8E1; border-radius: 8px; padding: 12px; }
                .tips h3 { border-bottom-color: #FFB300; }
                .tip { font-size: 12px; padding: 4px 0; color: #666; }
                .footer { margin-top: 30px; padding-top: 16px; border-top: 1px solid #eee; text-align: center; color: #999; font-size: 10px; }
                .footer .trip-id { font-family: monospace; color: #bbb; margin-top: 4px; font-size: 9px; }
                @media print { 
                    body { padding: 20px; font-size: 11px; } 
                    .instructions { display: none; }
                    .section { page-break-inside: avoid; }
                }
            </style>
        </head>
        <body>
            <div class="logo">PERCORO</div>
            ${forPDF ? '<div class="instructions"><strong>To save as PDF:</strong> Press Ctrl+P (Cmd+P on Mac) → Select "Save as PDF" as destination → Click Save</div>' : ''}
            
            <h1>${trip.title}</h1>
            <div class="destination">📍 ${trip.destination}</div>
            
            <div class="meta-grid">
                <div class="meta-item">
                    <div class="meta-label">Dates</div>
                    <div class="meta-value">${fmtDate(trip.startDate)} - ${fmtDate(trip.endDate)}</div>
                </div>
                <div class="meta-item">
                    <div class="meta-label">Status</div>
                    <div class="meta-value"><span class="status-badge ${trip.status === 'planning' ? 'info' : ''}">${trip.status}</span></div>
                </div>
                <div class="meta-item">
                    <div class="meta-label">Type</div>
                    <div class="meta-value">${trip.isGroup ? 'Group Trip' : 'Individual'}</div>
                </div>
                <div class="meta-item">
                    <div class="meta-label">Visibility</div>
                    <div class="meta-value">${getVisibilityLabel(trip.visibility)}</div>
                </div>
            </div>
            
            ${overviewHTML}
            
            ${trip.description ? `<div class="description"><strong>Description:</strong> ${trip.description}</div>` : ''}
            
            ${travelTipsHTML}
            
            ${flightsHTML}
            ${hotelsHTML}
            ${transportHTML}
            ${activitiesHTML}
            
            ${itineraryHTML}
            
            <div class="footer">
                <div>Generated from Percoro · percoro.com · ${formatDate(todayLocalString())}</div>
                <div class="trip-id">Trip ID: ${trip.tripId}</div>
            </div>
        </body>
        </html>
    `;
};

const handlePrintTrip = () => {
    const html = generateFullTripHTML(false);
    
    // Create hidden iframe for inline printing
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);
    
    const iframeDoc = iframe.contentWindow?.document;
    if (iframeDoc) {
        iframeDoc.open();
        iframeDoc.write(html);
        iframeDoc.close();
        
        // Wait for content to load then print
        setTimeout(() => {
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
            // Remove iframe after printing
            setTimeout(() => {
                document.body.removeChild(iframe);
            }, 1000);
        }, 300);
    }
};

const handleDownloadPDF = () => {
    const html = generateFullTripHTML(true);
    
    // Create hidden iframe for inline PDF
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);
    
    const iframeDoc = iframe.contentWindow?.document;
    if (iframeDoc) {
        iframeDoc.open();
        iframeDoc.write(html);
        iframeDoc.close();
        
        // Wait for content to load then print (user saves as PDF)
        setTimeout(() => {
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
            // Remove iframe after printing
            setTimeout(() => {
                document.body.removeChild(iframe);
            }, 1000);
        }, 300);
    }
};
    // AI Activity Recommendations
    const handleGetActivityRecommendations = async () => {
        if (!trip) return;
        setAiActivityLoading(true);
        try {
            const response = await api.post('/ai/suggest-activities', {
                tripId: trip.tripId,
                destination: trip.destination,
                startDate: trip.startDate,
                endDate: trip.endDate,
            });
            const data = await response.json();
            if (response.ok && data.success && data.activities) {
                const plan: AiTripPlan = {
                    title: trip.title,
                    description: `Activity recommendations for ${trip.destination}`,
                    flights: [],
                    hotels: [],
                    cars: [],
                    activities: data.activities,
                    itinerary: [],
                    budget: { flights: 0, hotels: 0, carRental: 0, activities: data.totalEstimatedCost || 0, foodEstimate: 0, miscEstimate: 0, total: data.totalEstimatedCost || 0, savingsTips: [] },
                    travelTips: [],
                };
                setAiActivityPlan(plan);
                setShowAiActivityReview(true);
            } else {
                setSnackbar({ open: true, message: data.error || 'Failed to get recommendations' });
            }
        } catch (err) {
            setSnackbar({ open: true, message: 'Network error' });
        } finally {
            setAiActivityLoading(false);
        }
    };

    const handleAiActivityApply = async (selectedPlan: AiTripPlan) => {
        if (!trip || !tripId) return;
        const promises = selectedPlan.activities
            .filter((act: any) => act.title)
            .map((act: any) =>
                api.post(`/trips/${tripId}/items`, {
                    item_type: 'activity',
                    title: act.title,
                    location: act.location || trip.destination,
                    start_date: act.date,
                    start_time: act.time,
                    estimated_cost: act.estimatedCostPerPerson,
                    notes: act.description,
                }).catch(() => null)
            );
        await Promise.all(promises);
        setShowAiActivityReview(false);
        setAiActivityPlan(null);
        await fetchTripItems();
        setSnackbar({ open: true, message: `${selectedPlan.activities.length} activities added!` });
    };

    // Helpers

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'planning': return 'info';
            case 'booked': return 'success';
            case 'completed': return 'default';
            case 'cancelled': return 'error';
            default: return 'default';
        }
    };

    const getVisibilityIcon = (visibility: string) => {
        switch (visibility) {
            case 'public': return <PublicIcon />;
            case 'bffs_travel_partners': return <PeopleIcon />;
            case 'friends_family': return <PeopleIcon />;
            default: return <LockIcon />;
        }
    };

    const getVisibilityLabel = (visibility: string) => {
        switch (visibility) {
            case 'public': return 'Public';
            case 'bffs_travel_partners': return 'BFFs/Travel Partners';
            case 'friends_family': return 'Friends & Family';
            default: return 'Private';
        }
    };

    // Calculate trip duration
    const tripDays = daysBetween(trip?.startDate, trip?.endDate);

    // RENDER
    if (loading) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error && !trip) {
        return (
            <Box sx={{ minHeight: '100vh', background: theme.palette.background.default }}>
                <AppBar position="sticky" color="transparent" elevation={0} sx={{ borderBottom: `1px solid ${theme.palette.divider}`, bgcolor: 'background.paper', zIndex: 10 }}>
                    <Toolbar>
                        <IconButton onClick={() => navigate('/trips')} sx={{ mr: 2 }}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Trip Not Found</Typography>
                    </Toolbar>
                </AppBar>
                <Container maxWidth="sm" sx={{ py: 4, textAlign: 'center' }}>
                    <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
                    <Button variant="contained" onClick={() => navigate('/trips')}>Back to My Trips</Button>
                </Container>
            </Box>
        );
    }

    // Define tab configuration based on trip type
    const isGroup = trip?.isGroup || false;

    const individualTabs = [
        { key: 'flight', icon: <FlightIcon />, label: 'Flights', count: flights.length, items: flights, itemType: 'flight' as const },
        { key: 'hotel', icon: <HotelIcon />, label: 'Hotels', count: hotels.length, items: hotels, itemType: 'hotel' as const },
        { key: 'transport', icon: <DirectionsCarIcon />, label: 'Transport', count: transport.length, items: transport, itemType: 'transport' as const },
        { key: 'activity', icon: <AttractionsIcon />, label: 'Activities', count: activities.length, items: activities, itemType: 'activity' as const },
    ];

    const groupPlanningTabs = [
        { key: 'hotel', icon: <HotelIcon />, label: 'Hotels', count: hotels.length, items: hotels, itemType: 'hotel' as const },
        { key: 'activity', icon: <AttractionsIcon />, label: 'Activities', count: activities.length, items: activities, itemType: 'activity' as const },
        { key: 'transport', icon: <DirectionsCarIcon />, label: 'Transport', count: transport.length, items: transport, itemType: 'transport' as const },
        { key: 'flight', icon: <FlightIcon />, label: 'Flights', count: flights.length, items: flights, itemType: 'flight' as const },
    ];

    const groupOnlyTabs = [
        { key: 'members', icon: <PeopleIcon />, label: 'Members', count: 0 },
        { key: 'chat', icon: <ChatIcon />, label: 'Chat', count: 0 },
        { key: 'polls', icon: <PollIcon />, label: 'Polls', count: 0 },
        { key: 'costsplit', icon: <AttachMoneyIcon />, label: 'Cost Split', count: 0 },
        { key: 'activityfeed', icon: <HistoryIcon />, label: 'Activity', count: 0 },
    ];

    const uploadTab = { key: 'upload', icon: <CloudUploadIcon />, label: 'Upload', count: 0 };
    const planningTabs = isGroup ? groupPlanningTabs : individualTabs;
    const allTabs = isGroup ? [...groupPlanningTabs, ...groupOnlyTabs] : [...individualTabs];

    return (
        <Box sx={{ minHeight: '100vh', background: theme.palette.background.default }}>
            {/* Top Navigation */}
            <AppBar position="sticky" color="transparent" elevation={0} sx={{ borderBottom: `1px solid ${theme.palette.divider}`, bgcolor: 'background.paper', zIndex: 10 }}>
                <Toolbar>
                    <IconButton onClick={() => navigate('/trips')} sx={{ mr: 2 }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
                        Trip Details
                    </Typography>
                    <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, alignItems: 'center', flexShrink: 0, flexWrap: { xs: 'wrap', sm: 'nowrap' }, justifyContent: 'flex-end' }}>
                        <Box sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'center', gap: 0.5 }}>
                            {trip && (displayStatus.label === 'Booked' ? (
                                <Chip icon={<CheckIcon />} label="Booked" size="small" sx={{ bgcolor: '#E8F5E9', color: '#2E7D32', fontWeight: 600, '& .MuiChip-icon': { color: '#4CAF50' } }} />
                            ) : (
                                <Chip label={displayStatus.label} size="small" color={displayStatus.color} sx={{ fontWeight: 600 }} />
                            ))}
                            <IconButton size="small" onClick={(e) => setTripMenuAnchorEl(e.currentTarget)} sx={{ color: 'text.secondary' }}>
                                <MoreVertIcon fontSize="small" />
                            </IconButton>
                        </Box>

                        {canBook && showBookHeader && (
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={!isSmall ? <ShoppingCartCheckoutIcon /> : undefined}
                                onClick={handleBookTrip}
                                sx={{
                                    bgcolor: '#FF6B35',
                                    borderRadius: '20px',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    px: { xs: 1.5, sm: 2 },
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    '&:hover': { bgcolor: '#E55A2B' },
                                }}
                            >
                                {isSmall ? 'Book' : 'Book Trip'}
                            </Button>
                        )}

                        <Menu anchorEl={tripMenuAnchorEl} open={Boolean(tripMenuAnchorEl)} onClose={() => setTripMenuAnchorEl(null)}
                            PaperProps={{ sx: { borderRadius: 2, minWidth: 180, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } }}>
                            {isOwner && !['cancelled','deleted'].includes(trip?.status||'') && (
                                <MenuItem onClick={() => { handleOpenShare(); setTripMenuAnchorEl(null); }} sx={{ fontSize: '0.85rem', gap: 1.5 }}>
                                    <ShareIcon sx={{ fontSize: 18 }} /> Share Trip
                                </MenuItem>
                            )}
                            {isOwner && !['cancelled','deleted'].includes(trip?.status||'') && (
                                <MenuItem onClick={() => { navigate(`/trips/${tripId}/photos`); setTripMenuAnchorEl(null); }} sx={{ fontSize: '0.85rem', gap: 1.5 }}>
                                    <PhotoLibraryOutlinedIcon sx={{ fontSize: 18 }} /> Add Photos
                                </MenuItem>
                            )}
                            <MenuItem onClick={() => { setShowItinerary(true); scrollToSection('itinerary-section'); setTripMenuAnchorEl(null); }} sx={{ fontSize: '0.85rem', gap: 1.5 }}>
                                <CalendarTodayOutlinedIcon sx={{ fontSize: 18 }} /> Itinerary
                            </MenuItem>
                            {!isLifecycleArchived && isOwner && (
                                <MenuItem onClick={() => { setTripPlanningExpanded(true); setPreviousActiveTab(activeTab); setUploadActive(true); scrollToSection('trip-planning-upload-area'); setTripMenuAnchorEl(null); }} sx={{ fontSize: '0.85rem', gap: 1.5 }}>
                                    <CloudUploadIcon sx={{ fontSize: 18 }} /> Upload Booking
                                </MenuItem>
                            )}
                            <Divider />
                            <MenuItem onClick={(e) => { setIdPopoverAnchorEl(e.currentTarget); setTripMenuAnchorEl(null); }} sx={{ fontSize: '0.85rem', gap: 1.5 }}>
                                <ContentCopyIcon sx={{ fontSize: 18 }} /> View Trip ID
                            </MenuItem>
                            <MenuItem onClick={() => { handlePrintTrip(); setTripMenuAnchorEl(null); }} sx={{ fontSize: '0.85rem', gap: 1.5 }}>
                                <PrintIcon sx={{ fontSize: 18 }} /> Print
                            </MenuItem>
                            <MenuItem onClick={() => { handleDownloadPDF(); setTripMenuAnchorEl(null); }} sx={{ fontSize: '0.85rem', gap: 1.5 }}>
                                <DownloadIcon sx={{ fontSize: 18 }} /> Download PDF
                            </MenuItem>
                            {isOwner && <Divider />}
                            {isOwner && (
                                <MenuItem onClick={() => { setEditOpen(true); setTripMenuAnchorEl(null); }} sx={{ fontSize: '0.85rem', gap: 1.5 }}>
                                    <EditOutlinedIcon sx={{ fontSize: 18 }} /> Edit Trip
                                </MenuItem>
                            )}
                            {isOwner && (
                                <MenuItem onClick={() => { setDeleteOpen(true); setTripMenuAnchorEl(null); }} sx={{ fontSize: '0.85rem', gap: 1.5, color: 'error.main' }}>
                                    <DeleteOutlineIcon sx={{ fontSize: 18 }} /> Delete Trip
                                </MenuItem>
                            )}
                        </Menu>
                    </Box>
                </Toolbar>
            </AppBar>

            <Container maxWidth="lg" sx={{ py: 3 }}>
                {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

                {trip && (
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={9}>
                            {/* 1. Trip Header Card with Quick Action Buttons */}
                            <Card sx={{ borderRadius: 3, mb: 3 }}>
                                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: { xs: 1.5, sm: 3 }, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, flexWrap: 'wrap', overflow: 'hidden' }}>
                                        {/* Left Side: Trip Info */}
                                        <Box sx={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden' }}>
                                            <Typography
                                                variant="h5"
                                                sx={{
                                                    fontWeight: 'bold',
                                                    mb: 0.5,
                                                    cursor: 'pointer',
                                                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                                                    '&:hover': { color: '#2D6E6A' }
                                                }}
                                                onClick={() => scrollToSection('trip-overview-section')}
                                            >
                                                {trip.title}
                                            </Typography>
                                            {/* Trip ID Popover */}
                                            <Popover
                                                open={Boolean(idPopoverAnchorEl)}
                                                anchorEl={idPopoverAnchorEl}
                                                onClose={() => setIdPopoverAnchorEl(null)}
                                                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                                PaperProps={{ sx: { borderRadius: 2, p: 2, maxWidth: 340 } }}
                                            >
                                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>Trip ID</Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#333', wordBreak: 'break-all' }}>{tripId}</Typography>
                                                    <IconButton size="small" onClick={() => { handleCopyTripId(); }} sx={{ color: idCopied ? '#2D6E6A' : 'text.secondary', flexShrink: 0 }}>
                                                        {idCopied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                                    </IconButton>
                                                </Box>
                                            </Popover>



                                            <Box sx={{ display: 'flex', gap: 1, my: 1 }}>
                                                <Chip
                                                    icon={getVisibilityIcon(trip.visibility)}
                                                    label={getVisibilityLabel(trip.visibility)}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            {trip.isCompanion && (
                                                <Chip
                                                    icon={<PeopleIcon />}
                                                    label={trip.companionType === 'travel_companion'
                                                        ? (trip.ownerName ? `Travel Companion of ${trip.ownerName}` : 'Travel Companion')
                                                        : (trip.ownerName ? `Shared with me by ${trip.ownerName}` : 'Shared with me')}
                                                    size="small"
                                                    sx={{ bgcolor: 'rgba(0,180,160,0.1)', color: '#2D6E6A', border: '1px solid rgba(0,180,160,0.3)', '& .MuiChip-icon': { color: '#2D6E6A' } }}
                                                />
                                            )}
                                            {isOwner && trip?.sharingPanel && trip.sharingPanel.length > 0 && (
                                                <Chip
                                                    icon={<PeopleIcon />}
                                                    label={`Shared with ${trip.sharingPanel.length}`}
                                                    size="small"
                                                    onClick={(e) => setSharingPanelAnchorEl(e.currentTarget as HTMLElement)}
                                                    sx={{ bgcolor: 'rgba(0,180,160,0.08)', color: '#2D6E6A', cursor: 'pointer', '&:hover': { bgcolor: 'rgba(0,180,160,0.15)' } }}
                                                />
                                            )}
                                                {trip.isGroup && (
                                                    <Chip
                                                        icon={<GroupIcon />}
                                                        label={trip.groupName || 'Group Trip'}
                                                        size="small"
                                                        sx={{ bgcolor: '#E3F2FD', color: '#1976D2' }}
                                                    />
                                                )}
                                            </Box>

                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                <LocationOnOutlinedIcon sx={{ color: '#FF6B6B', fontSize: 20 }} />
                                                <Typography variant="body1" sx={{ fontWeight: 500 }}>{trip.destination}</Typography>
                                            </Box>

                                            {(trip.startDate || trip.endDate) && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 0.5, sm: 1.5 }, color: 'text.secondary' }}>
                                                    <CalendarTodayOutlinedIcon sx={{ fontSize: 18, color: '#64B5F6' }} />
                                                    <Typography variant="body2">
                                                        {trip.startDate && formatDate(trip.startDate, { month: 'short', day: 'numeric', year: 'numeric' })}
                                                        {trip.endDate && ` - ${formatDate(trip.endDate, { month: 'short', day: 'numeric', year: 'numeric' })}`}
                                                    </Typography>
                                                </Box>
                                            )}

                                            {/* Mobile-only action row — Share Trip only */}
                                            {primaryCTA && (
                                            <Box sx={{ display: { xs: 'flex', sm: 'none' }, flexDirection: 'column', gap: 1, mt: 1.5 }}>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                {primaryCTA === 'book' && (
                                                    <Button variant="contained" size="small" startIcon={<ShoppingCartCheckoutIcon />} onClick={handleBookTrip}
                                                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, fontSize: '0.8rem', px: 2, py: 0.6, bgcolor: '#FF6B35', color: 'white', boxShadow: 'none', '&:hover': { bgcolor: '#E55A2B', boxShadow: 'none' } }}>
                                                        Book Trip
                                                    </Button>
                                                )}
                                                {primaryCTA === 'share' && (
                                                    <Button variant="contained" size="small" startIcon={<ShareIcon />} onClick={handleOpenShare}
                                                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', px: 2, py: 0.6, bgcolor: '#2D6E6A', color: 'white', boxShadow: 'none', '&:hover': { bgcolor: '#009688', boxShadow: 'none' } }}>
                                                        Share Trip
                                                    </Button>
                                                )}
                                                {primaryCTA === 'add_photos' && (
                                                    <Button variant="contained" size="small" startIcon={<PhotoLibraryOutlinedIcon />} onClick={() => navigate(`/trips/${tripId}/photos`)}
                                                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', px: 2, py: 0.6, bgcolor: '#2D6E6A', color: 'white', boxShadow: 'none', '&:hover': { bgcolor: '#009688', boxShadow: 'none' } }}>
                                                        Add Photos
                                                    </Button>
                                                )}
                                                </Box>
                                            </Box>
                                            )}
                                        </Box>

                                        {/* Right Side: status+menu row, then primary CTA below */}
                                        <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', gap: 1, alignItems: 'flex-end', mt: 0, width: 'auto', flexShrink: 0 }}>
                                            {/* Status chip + ⋯ menu — desktop only */}
                                            <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 0.5 }}>
                                                {displayStatus.label === 'Booked' ? (
                                                    <Chip icon={<CheckIcon />} label="Booked" size="small" sx={{ bgcolor: '#E8F5E9', color: '#2E7D32', fontWeight: 600, '& .MuiChip-icon': { color: '#4CAF50' } }} />
                                                ) : (
                                                    <Chip label={displayStatus.label} size="small" color={displayStatus.color} sx={{ fontWeight: 600 }} />
                                                )}
                                                <IconButton size="small" onClick={(e) => setTripMenuAnchorEl(e.currentTarget)} sx={{ color: 'text.secondary' }}>
                                                    <MoreVertIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                                {primaryCTA === 'book' && (
                                                <Button variant="contained" startIcon={<ShoppingCartCheckoutIcon />} onClick={handleBookTrip}
                                                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, fontSize: { xs: '0.75rem', sm: '0.9rem' }, px: { xs: 1.5, sm: 2.5 }, py: { xs: 0.75, sm: 1.2 }, bgcolor: '#FF6B35', color: 'white', boxShadow: 'none', '&:hover': { bgcolor: '#E55A2B', boxShadow: 'none' } }}>
                                                    Book Trip
                                                </Button>
                                                )}
                                                {primaryCTA === 'share' && (
                                                <Button variant="contained" startIcon={<ShareIcon />} onClick={handleOpenShare}
                                                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.9rem' }, px: { xs: 1.5, sm: 2.5 }, py: { xs: 0.75, sm: 1.2 }, bgcolor: '#2D6E6A', color: 'white', boxShadow: 'none', '&:hover': { bgcolor: '#009688', boxShadow: 'none' } }}>
                                                    Share Trip
                                                </Button>
                                                )}
                                                {primaryCTA === 'add_photos' && (
                                                <Button variant="contained" startIcon={<PhotoLibraryOutlinedIcon />} onClick={() => navigate(`/trips/${tripId}/photos`)}
                                                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.9rem' }, px: { xs: 1.5, sm: 2.5 }, py: { xs: 0.75, sm: 1.2 }, bgcolor: '#2D6E6A', color: 'white', boxShadow: 'none', '&:hover': { bgcolor: '#009688', boxShadow: 'none' } }}>
                                                    Add Photos
                                                </Button>
                                                )}
                                            </Box>

{/* Trip Intelligence Box */}
                                            {!isLifecycleArchived && (
                                            <Box sx={{
                                                display: { xs: 'none', sm: 'block' },
                                                border: '1px solid #E0E0E0',
                                                borderRadius: 2,
                                                p: { xs: 0.75, sm: 1 },
                                                bgcolor: '#FAFAFA',
                                                width: '100%'
                                            }}>
                                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1, display: 'block' }}>
                                                    Trip Intelligence
                                                </Typography>

                                                {(isInternational || flights.length > 0 || hotels.length > 0 || transport.length > 0) && (
                                                    <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                                                        {isInternational && (
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                onClick={() => {
                                                                    setShowTripIntelligence(true);
                                                                    setTripIntelligenceSection('requirements');
                                                                    scrollToSection('trip-intelligence-section');
                                                                }}
                                                                sx={{
                                                                    borderRadius: 2,
                                                                    textTransform: 'none',
                                                                    fontWeight: 600,
                                                                    fontSize: { xs: '0.75rem', sm: '0.85rem' },
                                                                    px: { xs: 1.5, sm: 2 },
                                                                    py: { xs: 0.5, sm: 0.8 },
                                                                    borderColor: '#FF9800',
                                                                    color: '#E65100',
                                                                    bgcolor: '#FFF3E0',
                                                                    '&:hover': { borderColor: '#E65100', bgcolor: '#FFE0B2' }
                                                                }}
                                                            >
                                                                Visa
                                                            </Button>
                                                        )}
                                                        {flights.length > 0 && (
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                onClick={() => {
                                                                    setShowTripIntelligence(true);
                                                                    setTripIntelligenceSection('airline');
                                                                    scrollToSection('trip-intelligence-section');
                                                                }}
                                                                sx={{
                                                                    borderRadius: 2,
                                                                    textTransform: 'none',
                                                                    fontWeight: 600,
                                                                    fontSize: '0.85rem',
                                                                    px: 2,
                                                                    py: 0.8,
                                                                    borderColor: '#E0E0E0',
                                                                    color: 'text.primary',
                                                                    '&:hover': { borderColor: '#2D6E6A', color: '#2D6E6A', bgcolor: '#E8F5F3' }
                                                                }}
                                                            >
                                                                Airline
                                                            </Button>
                                                        )}
                                                        {hotels.length > 0 && (
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                onClick={() => {
                                                                    setShowTripIntelligence(true);
                                                                    setTripIntelligenceSection('hotel');
                                                                    scrollToSection('trip-intelligence-section');
                                                                }}
                                                                sx={{
                                                                    borderRadius: 2,
                                                                    textTransform: 'none',
                                                                    fontWeight: 600,
                                                                    fontSize: '0.85rem',
                                                                    px: 2,
                                                                    py: 0.8,
                                                                    borderColor: '#E0E0E0',
                                                                    color: 'text.primary',
                                                                    '&:hover': { borderColor: '#2D6E6A', color: '#2D6E6A', bgcolor: '#E8F5F3' }
                                                                }}
                                                            >
                                                                Hotel
                                                            </Button>
                                                        )}
                                                        {transport.length > 0 && (
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                onClick={() => {
                                                                    setShowTripIntelligence(true);
                                                                    setTripIntelligenceSection('transport');
                                                                    scrollToSection('trip-intelligence-section');
                                                                }}
                                                                sx={{
                                                                    borderRadius: 2,
                                                                    textTransform: 'none',
                                                                    fontWeight: 600,
                                                                    fontSize: '0.85rem',
                                                                    px: 2,
                                                                    py: 0.8,
                                                                    borderColor: '#E0E0E0',
                                                                    color: 'text.primary',
                                                                    '&:hover': { borderColor: '#2D6E6A', color: '#2D6E6A', bgcolor: '#E8F5F3' }
                                                                }}
                                                            >
                                                                Car
                                                            </Button>
                                                        )}
                                                    </Box>
                                                )}

                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={() => {
                                                            setShowTripIntelligence(true);
                                                            setTripIntelligenceSection('weather');
                                                            scrollToSection('trip-intelligence-section');
                                                        }}
                                                        sx={{
                                                            borderRadius: 2,
                                                            textTransform: 'none',
                                                            fontWeight: 600,
                                                            fontSize: '0.85rem',
                                                            px: 2,
                                                            py: 0.8,
                                                            borderColor: '#E0E0E0',
                                                            color: 'text.primary',
                                                            '&:hover': { borderColor: '#2D6E6A', color: '#2D6E6A', bgcolor: '#E8F5F3' }
                                                        }}
                                                    >
                                                        Weather
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={() => {
                                                            setShowTripIntelligence(true);
                                                            setTripIntelligenceSection('packing');
                                                            scrollToSection('trip-intelligence-section');
                                                        }}
                                                        sx={{
                                                            borderRadius: 2,
                                                            textTransform: 'none',
                                                            fontWeight: 600,
                                                            fontSize: '0.85rem',
                                                            px: 2,
                                                            py: 0.8,
                                                            borderColor: '#E0E0E0',
                                                            color: 'text.primary',
                                                            '&:hover': { borderColor: '#2D6E6A', color: '#2D6E6A', bgcolor: '#E8F5F3' }
                                                        }}
                                                    >
                                                        Packing
                                                    </Button>
                                                    {showLocalEvents && (
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={() => scrollToSection('local-events-section')}
                                                        sx={{
                                                            borderRadius: 2,
                                                            textTransform: 'none',
                                                            fontWeight: 600,
                                                            fontSize: '0.85rem',
                                                            px: 2,
                                                            py: 0.8,
                                                            borderColor: '#E0E0E0',
                                                            color: 'text.primary',
                                                            '&:hover': { borderColor: '#2D6E6A', color: '#2D6E6A', bgcolor: '#E8F5F3' }
                                                        }}
                                                    >
                                                        Local Events
                                                    </Button>
                                                    )}
                                                </Box>
                                            </Box>
                                            )}
                                        </Box>
                                    </Box>

                                    {trip.description && (
                                        <Box sx={{ pt: 2, mt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Description</Typography>
                                            <Typography color="text.secondary" variant="body2">{trip.description}</Typography>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Sharing Popover — owner only, rendered inline in header chip row */}
                            {isOwner && trip?.sharingPanel && trip.sharingPanel.length > 0 && (
                                <>
                                <Popover
                                    open={Boolean(sharingPanelAnchorEl)}
                                    anchorEl={sharingPanelAnchorEl}
                                    onClose={() => setSharingPanelAnchorEl(null)}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                    PaperProps={{ sx: { borderRadius: 3, p: 1.5, minWidth: 280, maxWidth: 360, boxShadow: '0 4px 20px rgba(0,0,0,0.12)' } }}
                                >
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, px: 0.5 }}>Shared With</Typography>
                                    {trip.sharingPanel.slice(0, 3).map((entry: any) => (
                                        <Box key={entry.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1, borderBottom: '1px solid #f5f5f5' }}>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{entry.displayName || entry.email}</Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                                                    <Chip
                                                        label={entry.status === 'accepted' ? 'Accepted ✅' : entry.status === 'declined' ? 'Declined ❌' : 'Pending ⏳'}
                                                        size="small"
                                                        sx={{ height: 18, fontSize: '0.6rem', bgcolor: entry.status === 'accepted' ? '#E8F5E9' : entry.status === 'declined' ? '#FFEBEE' : '#FFF3E0', color: entry.status === 'accepted' ? '#2E7D32' : entry.status === 'declined' ? '#C62828' : '#E65100' }}
                                                    />
                                                    <Typography variant="caption" color="text.secondary">
                                                        {entry.companionType === 'travel_companion' ? 'Companion' : 'View only'}
                                                        {entry.expiresAt ? ` · Exp ${new Date(entry.expiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Button
                                                size="small"
                                                color="error"
                                                onClick={async () => {
                                                    if (!window.confirm(`Stop sharing with ${entry.displayName || entry.email}?`)) return;
                                                    try {
                                                        await api.delete(`/trips/${tripId}/companions/${entry.id}`);
                                                        await fetchTrip();
                                                        setSharingPanelAnchorEl(null);
                                                        setSnackbar({ open: true, message: 'Access removed' });
                                                    } catch { setSnackbar({ open: true, message: 'Failed to remove access' }); }
                                                }}
                                                sx={{ textTransform: 'none', fontSize: '0.7rem', ml: 1, minWidth: 'auto', px: 1 }}
                                            >
                                                Remove
                                            </Button>
                                        </Box>
                                    ))}
                                    {trip.sharingPanel.length > 3 && (
                                        <Box sx={{ pt: 1, borderTop: '1px solid #f5f5f5', mt: 0.5 }}>
                                            <Typography variant="caption" color="text.secondary">
                                                + {trip.sharingPanel.length - 3} more
                                            </Typography>
                                        </Box>
                                    )}
                                    <Box sx={{ pt: 1, borderTop: '1px solid #f0f0f0', mt: 0.5 }}>
                                        <Button
                                            size="small"
                                            onClick={() => { setSharingPanelAnchorEl(null); navigate(`/trips/${tripId}/sharing`); }}
                                            sx={{ textTransform: 'none', fontSize: '0.75rem', color: '#2D6E6A', p: 0 }}
                                        >
                                            View all & manage →
                                        </Button>
                                    </Box>
                                </Popover>
                                </>
                            )}

                            {/* Pending Photos — owner only */}
                            {isOwner && (trip?.pendingPhotos ?? 0) > 0 && (
                                <Card sx={{ borderRadius: 3, mb: 3, border: '1px solid #FFE082', bgcolor: '#FFFDE7' }}>
                                    <CardContent sx={{ p: 2 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                            Pending Photos ({trip?.pendingPhotos})
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            A travel companion has submitted photos for your approval.
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => navigate(`/trips/${tripId}/photos`)}
                                            sx={{ textTransform: 'none', borderRadius: 2, borderColor: '#FFB300', color: '#E65100' }}
                                        >
                                            Review Photos
                                        </Button>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Trip Photos Section (hero variant — completed/archived only, between Trip Header and Planning) */}
                            {isLifecycleArchived && (
                                <TripPhotosSection
                                    photos={photos}
                                    tripId={tripId || ''}
                                    variant="hero"
                                    onShareClick={handleOpenShare}
                                />
                            )}

                            {/* 2. Planning Dashboard */}
{!isLifecycleArchived && isOwner && (
<Paper id="trip-planning-section" elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden', mb: 3 }}>
                                <Box 
                                    sx={{ 
                                        bgcolor: isGroup ? '#1976D2' : '#2D6E6A', 
                                        color: 'white', 
                                        px: 2, 
                                        py: 1.5, 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: 1,
                                        cursor: 'pointer',
                                        '&:hover': { opacity: 0.95 }
                                    }}
                                    onClick={() => setTripPlanningExpanded(!tripPlanningExpanded)}
                                >
                                    {isGroup ? <GroupIcon /> : <AttractionsIcon />}
                                    <Typography variant="h6">
                                        {isGroup ? 'Group Dashboard' : 'Trip Planning'}
                                    </Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        {(flights.length + hotels.length + activities.length + transport.length) > 0 
                                            ? `(${flights.length + hotels.length + activities.length + transport.length} items)${!tripPlanningExpanded ? ' - Click to expand' : ''}`
                                            : !tripPlanningExpanded ? 'Click to start planning' : '(0 items)'}
                                    </Typography>
                                    <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {isGroup && (
                                            <Typography variant="body2" sx={{ mr: 1, opacity: 0.9 }}>
                                                0 members
                                            </Typography>
                                        )}
                                        {bookingEnabled && (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<ShoppingCartCheckoutIcon />}
                                            onClick={(e) => { e.stopPropagation(); handleBookTrip(); }}
                                            sx={{
                                                borderColor: 'rgba(255,255,255,0.7)',
                                                color: 'white',
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                borderRadius: 2,
                                                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                                            }}
                                        >
                                            Book
                                        </Button>
                                        )}
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<CloudUploadIcon />}
                                            onClick={(e) => { e.stopPropagation(); if (!uploadActive) setPreviousActiveTab(activeTab); setUploadActive(true); setTripPlanningExpanded(true); scrollToSection('trip-planning-upload-area'); }}
                                            sx={{
                                                borderColor: 'rgba(255,255,255,0.7)',
                                                color: 'white',
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                borderRadius: 2,
                                                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                                            }}
                                        >
                                            Upload
                                        </Button>
                                        <IconButton size="small" sx={{ color: 'white' }}>
                                            {tripPlanningExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                        </IconButton>
                                    </Box>
                                </Box>

                                <Collapse in={tripPlanningExpanded}>
                                    {uploadActive ? (
                                        <Box id="trip-planning-upload-area" sx={{ p: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Upload a booking</Typography>
                                                <Button size="small" onClick={() => { setUploadActive(false); setActiveTab(previousActiveTab); }} sx={{ textTransform: 'none' }}>
                                                    Close
                                                </Button>
                                            </Box>
                                            {tripId && <TripItemImportCard tripId={tripId} onItemsAdded={fetchTripItems} />}
                                        </Box>
                                    ) : (
                                    <><Tabs
                                    value={activeTab}
                                    onChange={(_, v) => setActiveTab(v)}
                                    variant="scrollable"
                                    scrollButtons="auto"
                                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                                >
                                    {allTabs.map((tab, idx) => (
                                        <Tab
                                            key={tab.key}
                                            icon={
                                                <Badge badgeContent={tab.count || undefined} color="secondary" sx={{ '& .MuiBadge-badge': { right: -3, top: 3 } }}>
                                                    {tab.icon}
                                                </Badge>
                                            }
                                            label={isSmall ? undefined : tab.label}
                                            iconPosition="start"
                                            sx={{ textTransform: 'none', minHeight: { xs: 48, sm: 56 }, minWidth: { xs: 50, sm: 90 }, px: { xs: 1, sm: 2 } }}
                                        />
                                    ))}
                                </Tabs>

                                <Box sx={{ p: 2, minHeight: 300 }}>
                                    {planningTabs.map((tab, idx) => (
                                        activeTab === idx && (
                                            <Box key={tab.key}>
                                                <PlanningTab
                                                    items={tab.items}
                                                    itemType={tab.itemType}
                                                    loading={itemsLoading}
                                                    onAdd={() => openAddDialog(tab.itemType)}
                                                    onEdit={openEditDialog}
                                                    onDelete={handleDeleteItem}
                                                    canEdit={isOwner}
                                                    bookingEnabled={bookingEnabled}
                                                    onBlockedClick={handleBookingBlocked}
                                                    onUpload={() => { if (!uploadActive) setPreviousActiveTab(activeTab); setUploadActive(true); setTripPlanningExpanded(true); scrollToSection('trip-planning-upload-area'); }}
                                                />
                                            </Box>
                                        )
                                    ))}

                                    {isGroup && activeTab === 4 && (
                                        <Alert severity="info">Members management coming soon. Use the Share button to invite people.</Alert>
                                    )}
                                    {isGroup && activeTab === 5 && (
                                        <Alert severity="info">Group chat coming soon.</Alert>
                                    )}
                                    {isGroup && activeTab === 6 && (
                                        <Alert severity="info">Polls coming soon.</Alert>
                                    )}
                                    {isGroup && activeTab === 7 && (
                                        <Alert severity="info">Cost splitting coming soon.</Alert>
                                    )}
                                    {isGroup && activeTab === 8 && (
                                        <Alert severity="info">Activity feed coming soon.</Alert>
                                    )}
                                </Box>
                                </>
                                    )}
                                </Collapse>
                            </Paper>
                            )}

                            {/* 3. Book CTA OR Upload Existing — single card with conditional content */}
                            {canShowUploadCard && (
                                <Card sx={{ borderRadius: 3, mb: 3, border: (showBookFooter && tripItems.length > 0) ? '2px solid #FF6B35' : '1px solid #E0E0E0', background: (showBookFooter && tripItems.length > 0) ? 'linear-gradient(135deg, #FFF8F5 0%, #FFF3EE 100%)' : '#FAFAFA' }}>
                                    <CardContent sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                                        <Box>
                                            <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                                                {(showBookFooter && tripItems.length > 0) ? 'Ready to book?' : 'Have an existing booking?'}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {(showBookFooter && tripItems.length > 0)
                                                    ? `You have ${tripItems.length} items planned with an estimated total of ${totalCost.toLocaleString()}`
                                                    : 'Upload your confirmation PDFs and we\u2019ll add the items to your trip.'}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'stretch' }}>
                                            {showBookFooter && tripItems.length > 0 && (
                                                <Button
                                                    variant="contained"
                                                    startIcon={<ShoppingCartCheckoutIcon />}
                                                    onClick={handleBookTrip}
                                                    sx={{
                                                        bgcolor: '#FF6B35',
                                                        borderRadius: 2,
                                                        textTransform: 'none',
                                                        fontWeight: 700,
                                                        px: 4,
                                                        py: 1.5,
                                                        boxShadow: '0 4px 14px rgba(255,107,53,0.4)',
                                                        '&:hover': { bgcolor: '#E55A2B' },
                                                    }}
                                                >
                                                    Book This Trip
                                                </Button>
                                            )}
                                            <Button
                                                variant={(showBookFooter && tripItems.length > 0) ? 'outlined' : 'contained'}
                                                startIcon={<CloudUploadIcon />}
                                                onClick={() => { if (!uploadActive) setPreviousActiveTab(activeTab); setUploadActive(true); setTripPlanningExpanded(true); scrollToSection('trip-planning-upload-area'); }}
                                                sx={{
                                                    bgcolor: (showBookFooter && tripItems.length > 0) ? 'transparent' : '#2D6E6A',
                                                    color: (showBookFooter && tripItems.length > 0) ? '#FF6B35' : 'white',
                                                    borderColor: (showBookFooter && tripItems.length > 0) ? '#FF6B35' : undefined,
                                                    borderRadius: 2,
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                    px: 3,
                                                    py: 1.2,
                                                    '&:hover': (showBookFooter && tripItems.length > 0)
                                                        ? { bgcolor: '#FFF8F5', borderColor: '#E55A2B' }
                                                        : { bgcolor: '#009688' },
                                                }}
                                            >
                                                Upload Booking
                                            </Button>
                                        </Box>
                                    </CardContent>
                                </Card>
                            )}

                            {['booked', 'booking'].includes(trip?.status || '') && (
                                <Card sx={{ borderRadius: 3, mb: 3, border: '2px solid #4CAF50', background: 'linear-gradient(135deg, #E8F5E9 0%, #F1F8E9 100%)' }}>
                                    <CardContent sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <CheckIcon sx={{ color: '#4CAF50', fontSize: 32 }} />
                                            <Box>
                                                <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#2E7D32' }}>
                                                    Trip Booked!
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Your trip to {trip.destination} is confirmed. Total: ${totalCost.toLocaleString()}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Button
                                            variant="outlined"
                                            onClick={() => navigate(`/trips/${tripId}/book`)}
                                            sx={{
                                                borderColor: '#4CAF50',
                                                color: '#4CAF50',
                                                borderRadius: 2,
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                '&:hover': { bgcolor: '#E8F5E9' },
                                            }}
                                        >
                                            View Booking Details
                                        </Button>
                                    </CardContent>
                                </Card>
                            )}

                            {/* 4. Trip Overview Stats */}
                            {isLifecycleArchived && (
                            <Card
                                id="trip-overview-section"
                                sx={{
                                    borderRadius: 3,
                                    mb: 3,
                                    bgcolor: 'rgba(0,180,160,0.05)',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    '&:hover': { bgcolor: 'rgba(0,180,160,0.08)' }
                                }}
                                onClick={() => setOverviewExpanded(!overviewExpanded)}
                            >
                                <CardContent sx={{ p: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            Trip Overview
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                {tripDays} days - {tripItems.length} items - ${totalCost.toLocaleString()}
                                            </Typography>
                                            <IconButton size="small">
                                                {overviewExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                            </IconButton>
                                        </Box>
                                    </Box>

                                    <Collapse in={overviewExpanded}>
                                        {uploadActive && isLifecycleArchived && (
                                            <Box id="trip-overview-upload-area" sx={{ p: 2, bgcolor: 'white', borderRadius: 2, mb: 2 }} onClick={(e) => e.stopPropagation()}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Upload a booking</Typography>
                                                    <Button size="small" onClick={() => { setUploadActive(false); }} sx={{ textTransform: 'none' }}>
                                                        Close
                                                    </Button>
                                                </Box>
                                                {tripId && <TripItemImportCard tripId={tripId} onItemsAdded={fetchTripItems} />}
                                            </Box>
                                        )}
                                        <Grid container spacing={2} sx={{ mt: 1 }}>
                                            <Grid item xs={4}>
                                                <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'white', borderRadius: 2 }}>
                                                    <CalendarTodayOutlinedIcon sx={{ color: '#2D6E6A', mb: 0.5 }} />
                                                    <Typography sx={{ fontWeight: 700 }}>{tripDays} Days</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {trip.startDate && formatDate(trip.startDate, { month: 'short', day: 'numeric' })}
                                                        {trip.endDate && ` - ${formatDate(trip.endDate, { month: 'short', day: 'numeric' })}`}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'white', borderRadius: 2 }}>
                                                    <AttractionsIcon sx={{ color: '#2D6E6A', mb: 0.5 }} />
                                                    <Typography sx={{ fontWeight: 700 }}>{tripItems.length} Items</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {hotels.length}h - {flights.length}f - {activities.length}a
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'white', borderRadius: 2 }}>
                                                    <AttachMoneyIcon sx={{ color: '#2D6E6A', mb: 0.5 }} />
                                                    <Typography sx={{ fontWeight: 700 }}>${totalCost.toLocaleString()}</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Estimated total
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>

                                        {trip?.status === 'cancelled' && (
                                            <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
                                                Trip Cancelled - items shown for record-keeping
                                            </Alert>
                                        )}

                                        <Box sx={{ mt: 2 }}>
                                            {(hotels.length + flights.length + activities.length + transport.length) === 0 ? (
                                                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3, fontStyle: 'italic' }}>
                                                    No items recorded for this trip.
                                                </Typography>
                                            ) : (
                                                <Box onClick={(e) => e.stopPropagation()}>
                                                    {[
                                                        { key: 'flights', label: 'Flights', icon: <FlightIcon sx={{ color: '#1976D2', fontSize: 18 }} />, items: flights },
                                                        { key: 'hotels', label: 'Hotels', icon: <HotelIcon sx={{ color: '#FF6B35', fontSize: 18 }} />, items: hotels },
                                                        { key: 'transport', label: 'Transport', icon: <DirectionsCarIcon sx={{ color: '#4A6FA5', fontSize: 18 }} />, items: transport },
                                                        { key: 'activities', label: 'Activities', icon: <AttractionsIcon sx={{ color: '#2D6E6A', fontSize: 18 }} />, items: activities },
                                                    ].filter(g => g.items.length > 0).map(group => (
                                                        <Box key={group.key} sx={{ mb: 2 }}>
                                                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                                                {group.icon}
                                                                {group.label} ({group.items.length})
                                                            </Typography>
                                                            <Box sx={{ mt: 0.5 }}>
                                                                {group.items.map((item: any, idx: number) => {
                                                                    const itemStart = item.startDate || item.start_date;
                                                                    const itemCost = item.estimatedCost ?? item.estimated_cost;
                                                                    return (
                                                                        <Box key={item.id || idx} sx={{ p: 1.25, bgcolor: 'white', borderRadius: 1.5, mb: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                                                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                                                                <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                                    {item.title || 'Untitled'}
                                                                                </Typography>
                                                                                {(item.location || itemStart) && (
                                                                                    <Typography variant="caption" color="text.secondary">
                                                                                        {item.location}{item.location && itemStart ? ' - ' : ''}{itemStart && formatDate(itemStart, { month: 'short', day: 'numeric' })}
                                                                                    </Typography>
                                                                                )}
                                                                            </Box>
                                                                            {typeof itemCost === 'number' && itemCost > 0 && (
                                                                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                                                                    ${itemCost.toLocaleString()}
                                                                                </Typography>
                                                                            )}
                                                                        </Box>
                                                                    );
                                                                })}
                                                            </Box>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            )}
                                        </Box>
                                    </Collapse>
                                </CardContent>
                            </Card>
                            )}

                            {/* 5. AI Recommendations CTA */}
                            {!isLifecycleArchived && aiActivityRecommendationsEnabled && (
                            <Card sx={{ borderRadius: 3, mb: 3, background: 'linear-gradient(135deg, #FFF8F5 0%, #FFF3EE 100%)', border: '1px solid #FFE0D0' }}>
                                <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                                    <Box>
                                        <Typography sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <AutoAwesomeIcon sx={{ color: '#FF6B35' }} />
                                            Get AI Activity Recommendations
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Let AI suggest activities, tours, and experiences for your trip
                                        </Typography>
                                    </Box>
                                    <Button
                                        variant="outlined"
                                        startIcon={aiActivityLoading ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                                        onClick={handleGetActivityRecommendations}
                                        disabled={aiActivityLoading}
                                        sx={{ borderColor: '#FF6B35', color: '#FF6B35', borderRadius: 2, textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#FFF8F5' } }}
                                    >
                                        {aiActivityLoading ? 'Generating...' : 'Suggest Activities'}
                                    </Button>
                                </CardContent>
                            </Card>
                            )}

                                                        {/* Companion Add Photos — shown to companions on active trips */}
                            {!isOwner && trip?.isCompanion && !isLifecycleArchived && (
                                <Card sx={{ borderRadius: 3, mb: 3, border: '1px solid rgba(0,180,160,0.3)', bgcolor: 'rgba(0,180,160,0.04)' }}>
                                    <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                                        <Box>
                                            <Typography sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <PhotoLibraryOutlinedIcon sx={{ color: '#2D6E6A', fontSize: 20 }} />
                                                Add Photos to This Trip
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Photos you add will be sent to the trip owner for approval before appearing in the trip.
                                            </Typography>
                                        </Box>
                                        <Button
                                            variant="contained"
                                            startIcon={<PhotoLibraryOutlinedIcon />}
                                            onClick={() => navigate(`/trips/${tripId}/photos`)}
                                            sx={{ bgcolor: '#2D6E6A', borderRadius: 2, textTransform: 'none', fontWeight: 600, boxShadow: 'none', '&:hover': { bgcolor: '#009688', boxShadow: 'none' } }}
                                        >
                                            Add Photos
                                        </Button>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Trip Photos Section (default variant — upcoming/ongoing only) */}
                            {!isLifecycleArchived && photos.length > 0 && (
                                <TripPhotosSection
                                    photos={photos}
                                    tripId={tripId || ''}
                                    variant="default"
                                />
                            )}

{/* 8. Day-by-Day Itinerary */}
{allItems.length > 0 && (
                                <Card
                                    id="itinerary-section"
                                    sx={{
                                        borderRadius: 3,
                                        mb: 3,
                                        cursor: 'pointer',
                                        bgcolor: showItinerary ? 'white' : '#FAFAFA',
                                        border: '1px dashed #ddd'
                                    }}
                                >
                                    <CardContent
                                        sx={{ p: 2, '&:last-child': { pb: 2 } }}
                                        onClick={() => setShowItinerary(!showItinerary)}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            Day-by-Day Itinerary ({allItems.length} items)
                                            </Typography>
                                            <IconButton size="small">
                                                {showItinerary ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                            </IconButton>
                                        </Box>
                                    </CardContent>

                                    <Collapse in={showItinerary}>
                                        <Box sx={{ px: 2, pb: 2 }}>
                                        <ItineraryTimeline
                                                items={allItems}
                                                startDate={trip.startDate}
                                                endDate={trip.endDate}
                                                destination={trip.destination}
                                            />
                                        </Box>
                                    </Collapse>
                                </Card>
                            )}
                            {/* 6. Trip Intelligence */}
                            <Card
                                id="trip-intelligence-section"
                                sx={{
                                    borderRadius: 3,
                                    mb: 3,
                                    cursor: 'pointer',
                                    bgcolor: showTripIntelligence ? 'white' : '#FAFAFA',
                                    border: '1px dashed #ddd'
                                }}
                            >
                                <CardContent
                                    sx={{ p: 2, '&:last-child': { pb: 2 } }}
                                    onClick={() => setShowTripIntelligence(!showTripIntelligence)}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            Trip Intelligence (Weather, Visa, Packing)
                                        </Typography>
                                        <IconButton size="small">
                                            {showTripIntelligence ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                        </IconButton>
                                    </Box>
                                </CardContent>

                                <Collapse in={showTripIntelligence}>
                                    <Box sx={{ px: 2, pb: 2 }}>
                                        <TripIntelligenceSection
                                            destination={trip.destination}
                                            startDate={trip.startDate}
                                            endDate={trip.endDate}
                                            tripTitle={trip.title}
                                            hasFlights={flights.length > 0}
                                            hasHotels={hotels.length > 0}
                                            hasTransport={transport.length > 0}
                                            flights={flights}
                                            hotels={hotels}
                                            transport={transport}
                                            activeSection={tripIntelligenceSection}
                                        />
                                    </Box>
                                </Collapse>
                            </Card>

                            {/* 7. Local Events Section */}
                            {showLocalEvents && (
                            <Card id="local-events-section" sx={{ borderRadius: 3, mb: 3, border: '1px dashed #ddd' }}>
                                <CardContent sx={{ p: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            Local Events in {trip.destination}
                                        </Typography>
                                        <Chip label="Coming Soon" size="small" sx={{ bgcolor: '#FFF3E0', color: '#E65100' }} />
                                    </Box>
                                    <Typography variant="body2" color="text.secondary">
                                        Discover concerts, sports games, festivals, and local happenings during your stay
                                        {trip.startDate && trip.endDate && (
                                            <> ({formatDate(trip.startDate, { month: 'short', day: 'numeric' })} - {formatDate(trip.endDate, { month: 'short', day: 'numeric' })})</>
                                        )}.
                                    </Typography>
                                    <Box sx={{ mt: 2, p: 2, bgcolor: '#FAFAFA', borderRadius: 2, textAlign: 'center' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Concerts - Sports - Theater - Exhibitions - Festivals
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                            )}

                        </Grid>

                        {/* Sidebar Ad - Desktop Only */}
                        {!isMobile && (
                            <Grid item md={3}>
                                <SidebarAd page="trip_details" contextCategory="hotel" />
                            </Grid>
                        )}
                    </Grid>
                )}
            </Container>

            {/* Search Dialogs */}
            <HotelSearchDialog
                open={hotelSearchOpen}
                onClose={() => setHotelSearchOpen(false)}
                tripId={tripId || ''}
                destination={trip?.destination || ''}
                checkIn={trip?.startDate || ''}
                checkOut={trip?.endDate || ''}
                guests={trip?.isGroup ? (Number(trip?.estimatedTravelers) || trip?.travelers || trip?.travelerCount || 4) : 2}
                isGroupTrip={trip?.isGroup || false}
                groupSize={trip?.isGroup ? (Number(trip?.estimatedTravelers) || trip?.travelers || trip?.travelerCount || 4) : undefined}
                onHotelsSelected={handleHotelsSelected}
            />

            <FlightSearchDialog
                open={flightSearchOpen}
                onClose={() => setFlightSearchOpen(false)}
                tripId={tripId || ''}
                origin=""
                destination={trip?.destination || ''}
                departDate={trip?.startDate || ''}
                returnDate={trip?.endDate || ''}
                passengers={1}
                onFlightsSelected={handleFlightsSelected}
            />

            <CarSearchDialog
                open={carSearchOpen}
                onClose={() => setCarSearchOpen(false)}
                tripId={tripId || ''}
                pickupLocation={trip?.destination || ''}
                pickupDate={trip?.startDate || ''}
                dropoffDate={trip?.endDate || ''}
                onCarsSelected={handleCarsSelected}
            />

            <ActivitySearchDialog
                open={activitySearchOpen}
                onClose={() => setActivitySearchOpen(false)}
                tripId={tripId || ''}
                destination={trip?.destination || ''}
                startDate={trip?.startDate || ''}
                endDate={trip?.endDate || ''}
                onActivitiesSelected={handleActivitiesSelected}
            />

            {/* Edit Item Dialog */}
            <ItemDialog
                isOpen={editDialogOpen}
                onClose={() => { setEditDialogOpen(false); setEditingItem(null); }}
                onSave={handleSaveItem}
                itemType={editDialogType}
                editItem={editingItem}
                destination={trip?.destination}
                startDate={trip?.startDate}
                endDate={trip?.endDate}
            />

            {/* Edit Trip Dialog */}
            <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold' }}>Edit Trip</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}
                        <TextField fullWidth label="Trip Title" name="title" value={editData.title} onChange={handleEditChange} sx={{ mb: 2 }} />
                        <TextField fullWidth label="Destination" name="destination" value={editData.destination} onChange={handleEditChange} sx={{ mb: 2 }} />
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={6}>
                                <TextField fullWidth label="Start Date" name="startDate" type="date" value={editData.startDate} onChange={handleEditChange} InputLabelProps={{ shrink: true }} />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField fullWidth label="End Date" name="endDate" type="date" value={editData.endDate} onChange={handleEditChange} InputLabelProps={{ shrink: true }} inputProps={{ min: editData.startDate || '' }} />
                            </Grid>
                        </Grid>
                        <TextField fullWidth label="Description" name="description" value={editData.description} onChange={handleEditChange} multiline rows={3} sx={{ mb: 2 }} />
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Status</InputLabel>
                                    <Select value={editData.status} label="Status" onChange={handleSelectChange('status')}>
                                        <MenuItem value="planning">Planning</MenuItem>
                                        <MenuItem value="booked">Booked</MenuItem>
                                        <MenuItem value="completed">Completed</MenuItem>
                                        <MenuItem value="cancelled">Cancelled</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Visibility</InputLabel>
                                    <Select value={editData.visibility} label="Visibility" onChange={handleSelectChange('visibility')}>
                                        <MenuItem value="private">Private</MenuItem>
                                        {friendsTripsEnabled && <MenuItem value="bffs_travel_partners">BFFs/Travel Partners</MenuItem>}
                                        {friendsTripsEnabled && <MenuItem value="friends_family">Friends & Family</MenuItem>}
                                        {publicTripsEnabled && <MenuItem value="public">Public</MenuItem>}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                        {editData.visibility !== 'private' && (
                            <TextField
                                fullWidth
                                label="Visibility expires (optional)"
                                type="date"
                                size="small"
                                value={editExpiresAt}
                                onChange={(e) => setEditExpiresAt(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ min: new Date().toISOString().split('T')[0] }}
                                helperText="Leave blank for no expiry"
                                sx={{ mt: 2 }}
                            />
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2.5 }}>
                    <Button onClick={() => setEditOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleEditSubmit} disabled={editLoading}>
                        {editLoading ? 'Saving...' : 'Save Changes'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Share Dialog */}
            <Dialog open={shareOpen} onClose={() => setShareOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ShareIcon sx={{ color: '#2D6E6A' }} /> Share Trip
                </DialogTitle>
                <DialogContent sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    {shareError && <Alert severity="error" sx={{ mb: 1 }}>{shareError}</Alert>}
                    <Box sx={{ mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Share Link</Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField fullWidth size="small" value={`${window.location.origin}/trips/${tripId}`} InputProps={{ readOnly: true }} />
                            <Button variant={linkCopied ? 'contained' : 'outlined'} onClick={handleCopyLink} startIcon={linkCopied ? <CheckIcon /> : <ContentCopyIcon />} sx={{ minWidth: 100, bgcolor: linkCopied ? '#2D6E6A' : undefined, color: linkCopied ? 'white' : '#2D6E6A' }}>
                                {linkCopied ? 'Copied!' : 'Copy'}
                            </Button>
                        </Box>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Add a personal note</Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={2}
                        value={shareNote}
                        onChange={(e) => setShareNote(e.target.value)}
                        placeholder={`Hey! I'm planning my ${trip?.title || 'trip'} and wanted to share it with you...`}
                        inputProps={{ maxLength: 500 }}
                        helperText={`${shareNote.length}/500`}
                        sx={{ mb: 1 }}
                    />
                    {['planning', 'upcoming', 'booked'].includes(trip?.status || '') && !isLifecycleArchived && (
                    <><Divider sx={{ my: 1.5 }} />
                    <Box sx={{ mb: 1.5 }}>
                        <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Invite as Travel Companion</Typography>
                                    <Typography variant="caption" color="text.secondary">Companion can add photos and contribute to the trip</Typography>
                                </Box>
                                <Checkbox
                                    checked={inviteAsCompanion}
                                    onChange={(e) => {
                                        if (e.target.checked && !canUse('trip_companions')) {
                                            setUpgradeFeatureName('Trip Companions');
                                            setUpgradeModalOpen(true);
                                            return;
                                        }
                                        setInviteAsCompanion(e.target.checked);
                                    }}
                                    sx={{ color: '#2D6E6A', '&.Mui-checked': { color: '#2D6E6A' } }}
                                />
                            </Box>
                            <TextField
                                fullWidth
                                label="Access expires (optional)"
                                type="date"
                                size="small"
                                value={shareExpiresAt}
                                onChange={(e) => setShareExpiresAt(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ min: new Date().toISOString().split('T')[0] }}
                                sx={{ mb: 1 }}
                            />
                            {shareExpiresAt && (
                                <Box sx={{ pl: 1, borderLeft: '2px solid #2D6E6A', mb: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography variant="caption">Notify companion before expiry</Typography>
                                        <Checkbox
                                            size="small"
                                            checked={notifyBeforeExpiry}
                                            onChange={(e) => setNotifyBeforeExpiry(e.target.checked)}
                                            sx={{ py: 0, color: '#2D6E6A', '&.Mui-checked': { color: '#2D6E6A' } }}
                                        />
                                    </Box>
                                    {notifyBeforeExpiry && (
                                        <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                                            {([1, 3, 7] as const).map(d => (
                                                <Chip
                                                    key={d}
                                                    label={`${d} day${d > 1 ? 's' : ''} before`}
                                                    size="small"
                                                    onClick={() => setNotifyDaysBefore(d)}
                                                    variant={notifyDaysBefore === d ? 'filled' : 'outlined'}
                                                    sx={notifyDaysBefore === d ? { bgcolor: '#2D6E6A', color: 'white' } : {}}
                                                />
                                            ))}
                                        </Box>
                                    )}
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography variant="caption">Ask companion to keep or delete their photos</Typography>
                                        <Checkbox
                                            size="small"
                                            checked={askPhotoAction}
                                            onChange={(e) => setAskPhotoAction(e.target.checked)}
                                            sx={{ py: 0, color: '#2D6E6A', '&.Mui-checked': { color: '#2D6E6A' } }}
                                        />
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Box></>
                    )}
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Share with friends
                    </Typography>
                    {friendsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                            <CircularProgress size={24} sx={{ color: '#2D6E6A' }} />
                        </Box>
                    ) : friends.filter(f => f.status === 'active').length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                            You don't have any friends yet. Add friends from the Friends page to share trips.
                        </Typography>
                    ) : (
                        <List dense sx={{ maxHeight: 220, overflowY: 'auto' }}>
                            {friends.filter(f => f.status === 'active').map(friend => {
                                const isSelected = selectedFriends.includes(friend.id);
                                return (
                                    <ListItemButton
                                        key={friend.id}
                                        onClick={() => {
                                            setSelectedFriends(prev =>
                                                isSelected
                                                    ? prev.filter(id => id !== friend.id)
                                                    : [...prev, friend.id]
                                            );
                                        }}
                                        sx={{ borderRadius: 1, mb: 0.5 }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 40 }}>
                                            <Checkbox
                                                edge="start"
                                                checked={isSelected}
                                                tabIndex={-1}
                                                disableRipple
                                                sx={{ color: '#2D6E6A', '&.Mui-checked': { color: '#2D6E6A' } }}
                                            />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={friend.name}
                                            secondary={friend.email}
                                            primaryTypographyProps={{ fontWeight: 500 }}
                                        />
                                    </ListItemButton>
                                );
                            })}
                        </List>
                    )}
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Invite someone new to Percoro
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        They'll get a friend invite + trip share. Once they join, they'll appear in your Friends list.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <TextField size="small" label="Their name" value={newShareName} onChange={(e) => setNewShareName(e.target.value)} sx={{ flex: 1 }} />
                        <TextField size="small" label="Email address" type="email" value={newShareEmail} onChange={(e) => setNewShareEmail(e.target.value)} sx={{ flex: 1.5 }} />
                        <Button size="small" variant="outlined" disabled={!newShareName.trim() || !newShareEmail.trim() || !newShareEmail.includes('@')}
                            onClick={() => { setNewShareEmails(prev => [...prev, { name: newShareName.trim(), email: newShareEmail.trim() }]); setNewShareName(''); setNewShareEmail(''); }}
                            sx={{ borderColor: '#2D6E6A', color: '#2D6E6A', textTransform: 'none', whiteSpace: 'nowrap' }}>
                            Add
                        </Button>
                    </Box>
                    {newShareEmails.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                            {newShareEmails.map((e, i) => (
                                <Chip key={i} label={e.name} size="small" onDelete={() => setNewShareEmails(prev => prev.filter((_, idx) => idx !== i))}
                                    sx={{ bgcolor: '#E8F5F3', color: '#2D6E6A' }} />
                            ))}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2.5 }}>
                    <Button onClick={() => setShareOpen(false)} disabled={shareLoading}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleShareSubmit}
                        disabled={shareLoading || friendsLoading || (selectedFriends.length === 0 && newShareEmails.length === 0)}
                        startIcon={<ShareIcon />}
                        sx={{ bgcolor: '#2D6E6A', '&:hover': { bgcolor: '#009688' } }}
                    >
                        {shareLoading ? 'Sending...' : selectedFriends.length > 0 ? `Send to ${selectedFriends.length} Friend${selectedFriends.length !== 1 ? 's' : ''}` : 'Done'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold' }}>Delete Trip</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>Are you sure you want to delete <strong>"{trip?.title}"</strong>?</Typography>
                    <Alert severity="warning">This action cannot be undone.</Alert>
                </DialogContent>
                <DialogActions sx={{ p: 2.5 }}>
                    <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDelete} disabled={deleteLoading}>
                        {deleteLoading ? 'Deleting...' : 'Delete Trip'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* AI Review Modal */}
            <AiResultsReviewModal
                open={showAiActivityReview}
                onClose={() => { setShowAiActivityReview(false); setAiActivityPlan(null); }}
                plan={aiActivityPlan}
                onApply={handleAiActivityApply}
                mode="phase2"
            />

            {/* Add Trip Item Dialog (manual entry when booking is off) */}
            <AddTripItemDialog
                open={addItemOpen}
                onClose={() => setAddItemOpen(false)}
                tripId={tripId || 
''
}
                tripDestination={trip?.destination}
                tripStartDate={trip?.startDate}
                tripEndDate={trip?.endDate}
                defaultType={addItemDefaultType}
                onItemAdded={fetchTripItems}
            />

            {/* Plan Upgrade Modal */}
            <PlanUpgradeModal open={upgradeModalOpen} onClose={() => setUpgradeModalOpen(false)} featureName={upgradeFeatureName} />

            {/* Snackbar */}
            <SnackbarMui
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                message={snackbar.message}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            />
        </Box>
    );
};

export default TripDetailPage;
